import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PillTabs from '../components/PillTabs'
import FarmFeedTable from './feed/FarmFeedTable'
import DeletedLotRowsLine, { DELETED_LOT_ROW_STYLE } from '../components/DeletedLotRowsLine'
import { useDeletedLotRowMatcher } from '../hooks/useDeletedLotRowMatcher'
import { withoutDeletedLotLogs } from '../utils/deletedLots'
import { useLocation } from 'react-router-dom'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import DateTimeLocalInput from '../components/DateTimeLocalInput'
import type { MatrixData } from '../components/MatrixTable'
import { getOfflineDb } from '../offline/dbFactory'
import { drainEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { useLots } from '../hooks/useLots'
import { useCrossFarmLotHistorySupplement } from '../hooks/useCrossFarmLotHistorySupplement'
import { useLotLineageProjection } from '../hooks/useLotLineageProjection'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import type { FeedLog, FishStockLog, LotCostEvent, MortalityLog, WeightSampleLog } from '../types/production'
import type { LotLineageProjectionRow } from '../types/lot'
import type { EntityBatchSyncStatus, EntityMutationResult, EntityMutationSyncState } from '../utils/entityMutationSync'
import { ensureFarmList } from '../utils/farmWizard'
import { feedLogApi } from '../utils/feedLogApi'
import { fishStockLogApi } from '../utils/fishStockLogApi'
import { lotCostEventApi } from '../utils/lotCostEventApi'
import { formatCostEventDisplayAmount } from './lots/lotCostForm'
import { mortalityLogApi } from '../utils/mortalityLogApi'
import { weightSampleLogApi } from '../utils/weightSampleLogApi'
import { overlayFishStockOnProductionRows } from './biomass/fishStockLogBridge'
import { overlayMortalityLogsOnProductionRows } from './biomass/mortalityLogBridge'
import { overlayWeightSamplesOnProductionRows } from './biomass/weightSampleLogBridge'
import { CrewCorrectionButton } from './crewCorrection/CrewCorrectionButton'
import { CrewRowBadges, useCorrectionCounts } from './crewCorrection/CrewRowBadges'
import { useActorNames } from '../utils/actorNames'
import { deriveFeedSyncOutcome, type FeedSyncOutcome } from '../utils/feedSyncOutcome'
import { computeSyncBannerVisibility } from '../utils/syncBannerVisibility'
import { readScopedString, writeScopedString } from '../utils/scopedStorage'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import { formatZonedWeekDisplay, getTimeZoneFromFarm, getZonedIsoDate, getZonedWeekKey, resolveTimeZone } from '../utils/timeBucketing'
import { formatStoredDateTimeAsLocal } from '../utils/dateNormalization'
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import { selectLatestDatedRow, selectNearestDatedRow } from '../utils/feedKpiSelection'
import { addFeedAliasMapping, resolveFeedPrimaryKeyForWaterRow, type FeedAliasIndex } from '../utils/feedWaterKeyResolution'
import {
  getDefaultMatrixForSpecies,
  interpolateFromMatrix,
  loadCachedMatrix,
  resolveSfrMatrixPresetKey
} from '../utils/sfrMatrix'
import { loadSfrMatrix } from '../utils/sfrMatrixApi'
import { resolveEffectiveSfrMatrix } from '../utils/sfrProfiles'
import { loadWaterTableRows, refreshWaterTableRowsFromApi, type WaterDataRow } from '../utils/waterTable'
import { buildFeedWeeklyBiomassResolver } from '../utils/feedWeeklyBiomass'
import { computeFcrPlanned, computeProjectedWeeklyFeedKg, computeSgrFromSamples } from '../utils/lotGrowthFormulas'
import { findFarmByIdOrName, findProductionUnitByIdOrName } from '../utils/contextSelection'
import {
  listProductionUnitTableRows,
  refreshProductionUnitTableFromApi,
  type BiomassDataRow,
  type ProductionUnitTableContext
} from '../utils/productionUnitTable'
import { computeDisplayedEndOfDayStockByRowId } from '../utils/productionEndOfDayStock'
import { filterInteractiveLots, isLotActiveForInteraction } from '../utils/lotInteraction'
import { resolveCanonicalLotKey, resolveLegacyLotIdentity, type LotIdentityCandidate } from '../utils/lotIdentity'
import { useT, type TranslationKey } from '../i18n'
import { loadStoredSpecies } from '../data/productionSpeciesPresets'
import type { ProductionUnit } from '../types/farm'
import {
  buildImportWorkspaceReviewNotice,
  parseImportWorkspaceReviewRouteState
} from './importWorkspace/importWorkspaceSession'
import { buildUnitSelectOptions, isUnitIdInactive } from '../utils/unitStatus'
import { dropEmptySameDayDuplicateRows } from '../utils/emptyDayRows'
import { readDayWeighing } from '../utils/dayWeighing'

type FeedEventDraft = {
  editingId: string | null
  recordedAt: string
  lot: string
  feedKg: string
  feedType: string
  notes: string
  feedingResponse: FeedingResponse | ''
}

// Office Plans v0 companion (findings #78): optional feeding-response score on
// web feed events (ASC 4.1.1.10.3, BAP 4.8). Crew Mode does not ask it.
type FeedingResponse = 'good' | 'normal' | 'poor' | 'none'
const FEEDING_RESPONSES: FeedingResponse[] = ['good', 'normal', 'poor', 'none']

const readFeedLogMetaString = (log: { metadata?: Record<string, unknown> }, key: string): string | undefined => {
  const value = log.metadata && typeof log.metadata === 'object' ? log.metadata[key] : undefined
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

const FEED_TYPE_SUGGESTION_WINDOW_DAYS = 90

// SFR interpolation function with temperature warnings (original matrix restored)
const interpolateSFR = (weight: number, temperature: number) => {
  // Original temperature range: 9-17°C (keeping the proven matrix)
  const temperatures = [9, 10, 11, 12, 13, 14, 15, 16, 17]
  const weights = [0.1, 0.45, 1.1, 2.8, 6.5, 8, 13, 22, 36, 80, 150, 300, 500, 1000]
  
  // Original proven SFR matrix (9-17°C)
  const sfrValues = [
    [4.90, 5.30, 5.70, 6.10, 6.70, 7.0, 7.8, 8, 8.1],
    [3.40, 3.80, 4.00, 4.20, 4.50, 5.0, 5.2, 5.5, 5.7],
    [2.80, 3.10, 3.30, 3.50, 3.80, 4.0, 4.3, 4.5, 4.6],
    [2.60, 2.70, 3.00, 3.20, 3.40, 3.5, 3.8, 4, 4.2],
    [2.50, 2.60, 2.90, 3.10, 3.30, 3.5, 3.7, 4, 4.2],
    [2.10, 2.20, 2.42, 2.60, 2.78, 3.0, 3.1, 3.6, 4],
    [1.87, 2.00, 2.16, 2.40, 2.55, 2.7, 2.8, 3.3, 3.5],
    [1.62, 1.70, 1.82, 1.90, 2.13, 2.2, 2.4, 2.8, 3],
    [1.29, 1.29, 1.50, 1.60, 1.80, 1.9, 2, 2.3, 2.8],
    [1.15, 1.15, 1.32, 1.40, 1.50, 1.6, 1.7, 1.9, 2],
    [1.00, 1.00, 1.18, 1.20, 1.32, 1.4, 1.5, 1.6, 2],
    [1.00, 1.00, 1.20, 1.20, 1.40, 1.4, 1.4, 1.5, 1.6],
    [0.90, 0.90, 1.10, 1.10, 1.20, 1.3, 1.3, 1.4, 1.4],
    [0.60, 0.60, 0.60, 0.60, 0.60, 0.7, 0.7, 0.8, 0.8]
  ]

  // Temperature status for warnings
  let temperatureStatus = 'optimal'
  let temperatureWarning = ''
  
  if (temperature < 7) {
    temperatureStatus = 'critical_cold'
    temperatureWarning = '🚨 CRITICAL: Temperature below 7°C - DO NOT FEED. Values shown are clamped to 7°C minimum.'
  } else if (temperature >= 7 && temperature < 9) {
    temperatureStatus = 'too_cold'
    temperatureWarning = '❄️ WARNING: Temperature too cold for optimal trout/salmon culture. Exercise extreme caution with feeding. Monitor DO concentration and saturation closely.'
  } else if (temperature > 17 && temperature <= 19) {
    temperatureStatus = 'too_warm'
    temperatureWarning = '🔥 WARNING: Temperature too warm for optimal trout/salmon culture. Exercise extreme caution with feeding. Monitor DO concentration and saturation closely.'
  } else if (temperature > 19) {
    temperatureStatus = 'critical_warm'
    temperatureWarning = '🚨 CRITICAL: Temperature above 19°C - DO NOT FEED. Values shown are clamped to 19°C maximum.'
  }

  // Extended clamping range (7-19°C) but use original matrix interpolation (9-17°C)
  let clampedTemp = temperature
  if (temperature < 7) {
    clampedTemp = 7
  } else if (temperature > 19) {
    clampedTemp = 19
  }
  
  // For temperatures outside 9-17°C range, clamp to matrix bounds for calculation
  const matrixClampedTemp = Math.max(temperatures[0], Math.min(temperatures[temperatures.length - 1], clampedTemp))
  const clampedWeight = Math.max(weights[0], Math.min(weights[weights.length - 1], weight))

  // Find weight indices for interpolation
  let weightIndex = 0
  for (let i = 0; i < weights.length - 1; i++) {
    if (clampedWeight <= weights[i + 1]) {
      weightIndex = i
      break
    }
  }

  // Find temperature indices for interpolation
  let tempIndex = 0
  for (let i = 0; i < temperatures.length - 1; i++) {
    if (matrixClampedTemp <= temperatures[i + 1]) {
      tempIndex = i
      break
    }
  }

  // Bilinear interpolation using original matrix
  const w1 = weights[weightIndex]
  const w2 = weights[Math.min(weightIndex + 1, weights.length - 1)]
  const t1 = temperatures[tempIndex] 
  const t2 = temperatures[Math.min(tempIndex + 1, temperatures.length - 1)]

  const q11 = sfrValues[weightIndex][tempIndex]
  const q12 = sfrValues[weightIndex][Math.min(tempIndex + 1, temperatures.length - 1)]
  const q21 = sfrValues[Math.min(weightIndex + 1, weights.length - 1)][tempIndex]
  const q22 = sfrValues[Math.min(weightIndex + 1, weights.length - 1)][Math.min(tempIndex + 1, temperatures.length - 1)]

  let interpolatedSFR
  if (w2 === w1 && t2 === t1) {
    interpolatedSFR = q11
  } else if (w2 === w1) {
    interpolatedSFR = q11 + (matrixClampedTemp - t1) * (q12 - q11) / (t2 - t1)
  } else if (t2 === t1) {
    interpolatedSFR = q11 + (clampedWeight - w1) * (q21 - q11) / (w2 - w1)
  } else {
    const r1 = q11 + (matrixClampedTemp - t1) * (q12 - q11) / (t2 - t1)
    const r2 = q21 + (matrixClampedTemp - t1) * (q22 - q21) / (t2 - t1)
    interpolatedSFR = r1 + (clampedWeight - w1) * (r2 - r1) / (w2 - w1)
  }
  
  return {
    sfr: interpolatedSFR,
    temperatureStatus,
    temperatureWarning,
    clampedTemp: clampedTemp !== temperature ? clampedTemp : null
  }
}

type DatabaseRow = {
  id: string
  lot: string
  stage: 'Active' | 'Inactive'
  date: string
  weight: string
  upVol: string
  mort: string
  fishStock: string
  do: string
}

export type FeedKPI = {
  lot: string
  week: string
  weekDisplay: string
  biomassKg: number
  sourceWeightG?: number
  sourceWeightDate?: string
  // The week's latest weight sample (any row of the week, not only the
  // latest row): SGR runs from it to the lot's next sample (findings #213).
  sampleWeightG?: number
  sampleDate?: string
  sourceTemperatureC?: number
  sourceTemperatureDate?: string
  sfrPercent: number
  dailyFeedKg: number
  weeklyFeedKg: number
  actualDailyFeedKg: number
  actualWeeklyFeedKg: number
  actualEventCount: number
  sgr?: number
  fcr?: number
  temperatureWarning?: string
  temperatureWarningKey?: TranslationKey
  temperatureWarningParams?: Record<string, string | number>
  temperatureStatus?: string
  clampedTemp?: number | null
  dataQuality: {
    hasWeight: boolean
    hasStock: boolean
    hasTemperature: boolean
    completeness: number
    missingData: TranslationKey[]
  }
}

type FeedLotIdentityLike = {
  lotId?: LotIdentityCandidate
  lot?: LotIdentityCandidate
}

const canonicalLotToken = (...candidates: LotIdentityCandidate[]): string => {
  const resolved = resolveCanonicalLotKey(...candidates)
  return resolved === 'unknown-lot' ? '' : resolved
}

const resolveFeedLotToken = (candidate: FeedLotIdentityLike): string => {
  const { token } = resolveLegacyLotIdentity(
    { source: 'lotId', value: candidate.lotId },
    { source: 'lot', value: candidate.lot }
  )
  return token === 'unknown-lot' ? '' : token
}

/**
 * Fase 7 (docs/tier-a-merge-consumer-mapping.md §2.6 line 364, consumer #15):
 * builds the token→lotId canonical map plus a parallel token→weight map from
 * the active lot roster and `LotLineageProjection` ancestor rows. Extracted
 * as a pure, exported function (rather than kept fully inline) so it is unit
 * testable in isolation — the surrounding `calculateKPIs` effect closure is
 * not otherwise a good unit boundary. Self entries are explicit weight `1`
 * (own biomass is never diluted); ancestor entries carry the fractional
 * weight from `LotLineageProjection` (first-weight-found per Option A).
 * Descendants are excluded from the ancestor fold-in — a descendant's own
 * token belongs to its own identity going forward.
 */
export const buildFeedCanonicalLotMaps = (args: {
  availableLots: readonly { id?: string | null; lotKey?: string | null }[]
  lotLineageRows: readonly LotLineageProjectionRow[]
}): { canonicalLotIdByToken: Map<string, string>; canonicalWeightByToken: Map<string, number> } => {
  const canonicalLotIdByToken = new Map<string, string>()
  const canonicalWeightByToken = new Map<string, number>()
  args.availableLots.forEach((lot) => {
    const lotId = String(lot.id ?? '').trim()
    if (!lotId) return
    const lotToken = resolveFeedLotToken({ lotId: lot.id, lot: lot.lotKey })
    if (!lotToken) return
    canonicalLotIdByToken.set(lotToken, lotId)
    canonicalWeightByToken.set(lotToken, 1)
  })
  args.lotLineageRows.forEach((row) => {
    if (row.relation === 'descendant') return
    const lotId = row.lotId.trim()
    if (!lotId) return
    const lotToken = resolveFeedLotToken({ lotId: row.memberLotId, lot: row.memberLotKey })
    if (!lotToken) return
    canonicalLotIdByToken.set(lotToken, lotId)
    canonicalWeightByToken.set(lotToken, row.weight)
  })
  return { canonicalLotIdByToken, canonicalWeightByToken }
}

/**
 * Findings #136: actual fed (FeedLog events) per KPI lot-week. Each event is
 * matched by lot id first, then by label, through the alias index the water
 * rows use — Crew Mode feedings may carry only `lotId`, and a label alone can
 * be stale after a rename. Exported for unit tests.
 */
export const assignActualFeedToKpis = (args: {
  feedEvents: ReadonlyArray<{ lotId?: string | null; lot?: string | null; recordedAt: string; feedKg?: unknown }>
  kpiByPrimaryKey: Map<string, Pick<FeedKPI, 'actualWeeklyFeedKg' | 'actualDailyFeedKg' | 'actualEventCount'>>
  aliasIndex: FeedAliasIndex
  weekOf: (recordedAt: string) => string
  parseKg: (value: unknown) => number
}): void => {
  const actualByPrimaryKey = new Map<string, { totalKg: number; count: number }>()
  args.feedEvents.forEach((log) => {
    const week = args.weekOf(log.recordedAt)
    if (!week || week === 'Invalid-Date') return

    const tokenCandidates = [
      resolveFeedLotToken({ lotId: log.lotId ?? undefined }),
      resolveFeedLotToken({ lot: (log.lot ?? '').trim() })
    ].filter((candidate) => Boolean(candidate && candidate.trim() !== ''))
    const primaryKey = resolveFeedPrimaryKeyForWaterRow({
      tokenCandidates,
      week,
      aliasIndex: args.aliasIndex,
      hasPrimaryKey: (candidate) => args.kpiByPrimaryKey.has(candidate)
    })
    if (!primaryKey) return

    const kg = args.parseKg(log.feedKg)
    const existing = actualByPrimaryKey.get(primaryKey)
    if (!existing) {
      actualByPrimaryKey.set(primaryKey, { totalKg: kg, count: 1 })
      return
    }
    existing.totalKg += kg
    existing.count += 1
  })

  args.kpiByPrimaryKey.forEach((kpi, primaryKey) => {
    const entry = actualByPrimaryKey.get(primaryKey)
    const weekly = entry?.totalKg ?? 0
    kpi.actualWeeklyFeedKg = weekly
    kpi.actualDailyFeedKg = weekly / 7
    kpi.actualEventCount = entry?.count ?? 0
  })
}

const getWeekNumberInTimeZone = (date: string, timeZone: string): string => {
  const weekKey = getZonedWeekKey(date, timeZone)
  if (!weekKey) {
    console.warn('Invalid date:', date)
    return 'Invalid-Date'
  }
  return weekKey
}

const formatWeekDisplay = (date: string, week: string, timeZone: string): string => {
  if (week === 'Invalid-Date') return week
  return formatZonedWeekDisplay(date, week, timeZone)
}

type FeedView = 'farm' | 'unit'

export default function Feed() {
  const location = useLocation()
  const principal = useStoredPrincipal()
  const [feedView, setFeedView] = useState<FeedView>(() => (readScopedString('feed-view') === 'unit' ? 'unit' : 'farm'))
  const changeFeedView = (next: FeedView) => {
    setFeedView(next)
    writeScopedString('feed-view', next)
  }
  const t = useT()
  const canEditFeedEvents = !principal || principal.role === 'editor' || principal.role === 'admin'

  const principalRoleKey = useMemo<TranslationKey>(() => {
    const role = principal?.role ?? 'viewer'
    return `role.${role}` as TranslationKey
  }, [principal?.role])

  const principalRoleLabel = useMemo(() => t(principalRoleKey), [principalRoleKey, t])

  const deepLink = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      farmId: params.get('farmId')?.trim() || '',
      farmName: params.get('farmName')?.trim() || '',
      productionUnitId: params.get('productionUnitId')?.trim() || '',
      productionUnitName: params.get('productionUnitName')?.trim() || '',
      focusWeek: params.get('focusWeek')?.trim() || ''
    }
  }, [location.search])
  const importReviewState = useMemo(() => parseImportWorkspaceReviewRouteState(location.search), [location.search])
  const importReviewNotice = useMemo(() => {
    if (importReviewState.importSource !== 'imports') return null
    return buildImportWorkspaceReviewNotice({
      rowLabel: 'feed log',
      importedRows: importReviewState.importedRows,
      skippedRows: importReviewState.skippedRows,
      queuedRows: importReviewState.queuedRows,
      localOnlyRows: importReviewState.localOnlyRows,
      blockedRows: importReviewState.blockedRows,
      syncErrorMessage: importReviewState.syncErrorMessage,
      syncState: importReviewState.syncState
    })
  }, [importReviewState])

  // Findings #143: fixed decimals (the +/- controls were removed).
  const fcrDecimals = 2
  const biomassDecimals = 1
  const [farmId, setFarmId] = useState<string>('')
  const [productionUnitId, setProductionUnitId] = useState<string>('')
  const [viewMode, setViewMode] = useState<'daily' | 'weekly'>('weekly')
  const [panelMode, setPanelMode] = useState<'kpis' | 'breakdown' | 'charts'>('kpis')
  const [desktopLogVisible, setDesktopLogVisible] = useState(true)
  const [feedKPIs, setFeedKPIs] = useState<FeedKPI[]>([])
  const [visibleKpiWeeks, setVisibleKpiWeeks] = useState(12)
  const [availableFarms, setAvailableFarms] = useState<any[]>([])
  const [availableProductionUnits, setAvailableProductionUnits] = useState<any[]>([])
  const selectedUnitInactive = isUnitIdInactive(availableProductionUnits, productionUnitId)
  const [dataRevision, setDataRevision] = useState(0)
  const [productionRows, setProductionRows] = useState<BiomassDataRow[]>([])
  // v2.1 #32 — mirror Biomass MORT-12 overlay so the derived-stock chain
  // can advance through rows whose `dailyMortality` cell is blank but whose
  // canonical mortality lives in `MortalityLog` events. Without this, the
  // Feed KPI gate reports "missing stock" against rows that Database renders
  // as `N (derivado)`.
  const [storedMortalityLogs, setMortalityLogs] = useState<MortalityLog[]>([])
  // Post-T3-A (v2.2): `ProductionRecord.weightGrams` and `fishStock` columns
  // were retired — the row's `weight` / `fishStock` values are now populated
  // at render time by the WeightSampleLog / FishStockLog overlays. Feed KPIs
  // MUST chain both overlays alongside the MortalityLog overlay or the KPI
  // gate reports "Datos faltantes" against imported rows even though the
  // Database screen renders the same weights / stocks correctly (they read
  // from the same event streams via the shared bridges).
  const [storedWeightSampleLogs, setWeightSampleLogs] = useState<WeightSampleLog[]>([])
  const [storedFishStockLogs, setFishStockLogs] = useState<FishStockLog[]>([])

  const selectedFarm = useMemo(
    () => availableFarms.find((farm: any) => String(farm?.id ?? farm?.name ?? '') === String(farmId)),
    [availableFarms, farmId]
  )
  const selectedProductionUnit = useMemo(
    () => (selectedFarm ? findProductionUnitByIdOrName(selectedFarm, productionUnitId) as ProductionUnit | undefined : undefined),
    [productionUnitId, selectedFarm]
  )

  const farmSpeciesKey = useMemo(() => resolveFarmProductionSpeciesKey(selectedFarm) ?? 'global', [selectedFarm])
  const profileSpeciesKey = useMemo(() => resolveFarmProductionSpeciesKey(selectedFarm) ?? loadStoredSpecies(), [selectedFarm])

  const sfrMatrixPresetKey = useMemo(() => resolveSfrMatrixPresetKey(farmSpeciesKey), [farmSpeciesKey])

  const [sfrMatrix, setSfrMatrix] = useState<MatrixData>(() => (
    loadCachedMatrix('global') ?? getDefaultMatrixForSpecies('global')
  ))

  const stockMetricKey = useMemo<TranslationKey>(() => {
    return farmSpeciesKey === 'shrimp' ? 'feed.stockMetric.plCount' : 'feed.stockMetric.fishStock'
  }, [farmSpeciesKey])

  const stockMetricLabel = useMemo(() => t(stockMetricKey), [stockMetricKey, t])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const result = await loadSfrMatrix(sfrMatrixPresetKey, { preferRecentLocal: true })
      if (cancelled) return
      setSfrMatrix(result.matrix)

      if (result.errorMessage) {
        console.warn('Feed: failed to refresh SFR matrix; using local matrix', result.errorMessage)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [sfrMatrixPresetKey])

  const activeTimeZone = useMemo(() => {
    return resolveTimeZone(getTimeZoneFromFarm(selectedFarm))
  }, [selectedFarm])

  const {
    lots: availableLots,
    loading: lotsLoading,
    error: lotsError
  } = useLots({ farmId, productionUnitId, enabled: Boolean(farmId && productionUnitId) })

  // Fase 3 (tier-a-merge-consumer-mapping.md §2.6, consumer #16
  // feedWeeklyBiomass via #15/#16's token→lotId pattern): ancestor lot ids
  // from a cross-family merge, so `canonicalLotIdByToken` below resolves a
  // legacy feed token to the surviving lot instead of only ever knowing the
  // active roster.
  const { rows: lotLineageRows } = useLotLineageProjection({ farmId, enabled: Boolean(farmId) })

  // tier-a-move-transfer-location-addendum.md: productionRows/mortalityLogs/
  // weightSampleLogs/fishStockLogs above are all scoped to this single
  // (farmId, productionUnitId) -- a lot transferred here never has its
  // pre-transfer history fetched at all, so the weekly-biomass resolver
  // would see a lot with no history right after every transfer. Fetches a
  // small, targeted supplement only when a lot in view has actually moved.
  const activeLotIdsForSupplement = useMemo(
    () => availableLots.map((lot) => String(lot.id ?? '')),
    [availableLots]
  )
  const crossFarmSupplement = useCrossFarmLotHistorySupplement({
    farmId,
    productionUnitId,
    lotIds: activeLotIdsForSupplement,
    enabled: Boolean(farmId && productionUnitId)
  })

  const interactiveLots = useMemo(() => filterInteractiveLots(availableLots), [availableLots])
  // Findings #136: Crew Mode feedings may carry only lotId — show the lot's name.
  const lotLabelById = useMemo(
    () => new Map(availableLots.map((lot) => [String(lot.id ?? ''), String(lot.lotKey ?? '')] as const)),
    [availableLots]
  )

  const defaultContextSfrMatrix = useMemo(
    () => resolveEffectiveSfrMatrix({
      farm: selectedFarm,
      productionUnit: selectedProductionUnit,
      species: profileSpeciesKey,
      speciesDefaultMatrix: sfrMatrix
    }),
    [profileSpeciesKey, selectedFarm, selectedProductionUnit, sfrMatrix]
  )

  const sfrMatrixByCanonicalLot = useMemo(() => {
    const nextMap = new Map<string, MatrixData>()

    availableLots.forEach((lot) => {
      const canonicalLotKey = resolveFeedLotToken({
        lotId: lot.id,
        lot: lot.lotKey
      })
      if (!canonicalLotKey) {
        return
      }

      nextMap.set(canonicalLotKey, resolveEffectiveSfrMatrix({
        farm: selectedFarm,
        productionUnit: selectedProductionUnit,
        lot,
        species: profileSpeciesKey,
        speciesDefaultMatrix: sfrMatrix
      }))
    })

    return nextMap
  }, [availableLots, profileSpeciesKey, selectedFarm, selectedProductionUnit, sfrMatrix])

  const resolveFeedSfrMatrix = useCallback((lotIdCandidate: unknown, lotLabel: unknown): MatrixData => {
    const canonicalLotKey = resolveFeedLotToken({
      lotId: lotIdCandidate as string | number | null | undefined,
      lot: String(lotLabel ?? '')
    })
    return (canonicalLotKey ? sfrMatrixByCanonicalLot.get(canonicalLotKey) : null) ?? defaultContextSfrMatrix
  }, [defaultContextSfrMatrix, sfrMatrixByCanonicalLot])

  const [feedEventsAll, setFeedEventsAll] = useState<FeedLog[]>([])
  const [feedEvents, setFeedEvents] = useState<FeedLog[]>([])
  // Findings #206: a deleted lot's feed is kept but hidden behind "Mostrar" in
  // Eventos recientes, and left out of the weekly figures (as in Biomasa).
  const deletedLotFarmIds = useMemo(() => (farmId ? [farmId] : []), [farmId])
  const isDeletedLotRow = useDeletedLotRowMatcher(deletedLotFarmIds)
  const [showDeletedLotFeed, setShowDeletedLotFeed] = useState(false)
  const isDeletedLotFeed = useCallback(
    (log: { lotId?: string | null; lot?: string | null; productionUnitId?: string | null }) => isDeletedLotRow(log, productionUnitId),
    [isDeletedLotRow, productionUnitId]
  )
  const currentFeedEventsAll = useMemo(() => feedEventsAll.filter((log) => !isDeletedLotFeed(log)), [feedEventsAll, isDeletedLotFeed])
  // Findings #207: a deleted lot's deaths, weights and counts never count for
  // an active lot with the same name (biomass and stock behind the feed plan).
  const mortalityLogs = useMemo(() => withoutDeletedLotLogs(storedMortalityLogs, isDeletedLotRow), [storedMortalityLogs, isDeletedLotRow])
  const weightSampleLogs = useMemo(() => withoutDeletedLotLogs(storedWeightSampleLogs, isDeletedLotRow), [storedWeightSampleLogs, isDeletedLotRow])
  const fishStockLogs = useMemo(() => withoutDeletedLotLogs(storedFishStockLogs, isDeletedLotRow), [storedFishStockLogs, isDeletedLotRow])
  const hiddenDeletedLotFeedCount = useMemo(() => feedEvents.filter((log) => isDeletedLotFeed(log)).length, [feedEvents, isDeletedLotFeed])
  const visibleFeedEvents = useMemo(
    () => (showDeletedLotFeed ? feedEvents : feedEvents.filter((log) => !isDeletedLotFeed(log))),
    [feedEvents, showDeletedLotFeed, isDeletedLotFeed]
  )
  const [feedEventsLoading, setFeedEventsLoading] = useState(false)
  const [feedEventsError, setFeedEventsError] = useState<string | null>(null)
  // T3-B-03d: cost badge lookup — indexes paired LotCostEvent by FeedLog id.
  // Populated by an effect below that fetches LotCostEvents scoped to the
  // current facility (sourceEventType='feedLog') whenever the feedEvents
  // list changes. Empty map means no badges render.
  const [costsByFeedLogId, setCostsByFeedLogId] = useState<Map<string, LotCostEvent>>(new Map())
  const [feedEventDraft, setFeedEventDraft] = useState<FeedEventDraft>(() => ({
    editingId: null,
    recordedAt: new Date().toISOString(),
    lot: '',
    feedKg: '',
    feedType: '',
    notes: '',
    feedingResponse: ''
  }))
  const [feedEventSaving, setFeedEventSaving] = useState(false)
  const [feedEventSaveError, setFeedEventSaveError] = useState<string | null>(null)
  // v2.1 finding #83: iOS Chrome silently suppresses `window.confirm()` in
  // some event-handler contexts (WebKit dialog gating), so `Eliminar` on
  // the Recent events row appeared unresponsive on cellphone-only operators.
  // Replaced the blocking `confirm()` with a two-step in-app confirm
  // mirroring the `LotCostSection` pattern: first tap flips the button
  // to the "Confirmar eliminar" red state; second tap fires the delete.
  // Findings #55: Crew / Corregido badges on the events table.
  const actorNames = useActorNames()
  const [feedCorrectionsRefreshKey, setFeedCorrectionsRefreshKey] = useState(0)
  const feedCorrectionCounts = useCorrectionCounts(
    'feed-log',
    feedEvents.map((log) => log.id),
    feedCorrectionsRefreshKey
  )
  const [feedEventStatusMessage, setFeedEventStatusMessage] = useState<string | null>(null)
  const [feedSyncStatus, setFeedSyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  const [feedSyncLastOutcome, setFeedSyncLastOutcome] = useState<(FeedSyncOutcome & { attemptedAtIso: string }) | null>(null)
  const [retryingFeedSync, setRetryingFeedSync] = useState(false)
  const feedEventStatusVersionRef = useRef(0)
  const pendingFeedSyncActionRef = useRef<'saved' | 'deleted' | null>(null)
  // v2.1 finding #61 (WSL-09, 2026-07-01): forward-ref indirection so
  // `applyFeedEventStatus` can call the outbox-snapshot refresher from its
  // `syncPromise.then` callback. Mirrors the Biomass fix. The `useEffect`
  // below `refreshCurrentFeedSyncStatus` keeps the ref pointed at the
  // latest identity.
  const refreshFeedSyncStatusFnRef = useRef<() => Promise<EntityBatchSyncStatus | null>>(async () => null)

  const [activePanel, setActivePanel] = useState<'log' | 'analyze'>('log')

  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(max-width: 430px)').matches
  })

  const clearFeedEventStatus = () => {
    feedEventStatusVersionRef.current += 1
    setFeedEventStatusMessage(null)
  }

  const getFeedEventStatusKey = (verb: 'saved' | 'deleted', state: EntityMutationSyncState): TranslationKey => {
    if (state === 'synced') return `feed.status.${verb}Synced` as TranslationKey
    if (state === 'offline') return `feed.status.${verb}Offline` as TranslationKey
    return `feed.status.${verb}Local` as TranslationKey
  }

  const refreshCurrentFeedEvents = useCallback(async (
    options?: { preferLocal?: boolean; forceRemote?: boolean }
  ): Promise<boolean> => {
    if (!farmId || !productionUnitId) {
      setFeedEventsAll([])
      setFeedEvents([])
      setFeedEventsLoading(false)
      setFeedEventsError(null)
      return false
    }

    setFeedEventsLoading(true)
    setFeedEventsError(null)

    try {
      const logs = await feedLogApi.list({ farmId, productionUnitId }, options)
      setFeedEventsAll(logs)
      setFeedEvents(logs.slice(0, 50))
      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : t('feed.error.unableToLoadEvents')
      setFeedEventsAll([])
      setFeedEvents([])
      setFeedEventsError(message)
      return false
    } finally {
      setFeedEventsLoading(false)
    }
  }, [farmId, productionUnitId, t])

  // T3-B-03d: hydrate the cost-badge lookup whenever the feed events list
  // changes. Batches a single lotCostEventApi.list() call per facility scope
  // and indexes the returned events (sourceEventType='feedLog') by
  // sourceEventId. Offline-first — falls back to Dexie mirror if remote fails.
  useEffect(() => {
    if (!farmId || feedEventsAll.length === 0) {
      setCostsByFeedLogId(new Map())
      return
    }
    let cancelled = false
    void lotCostEventApi
      .list({
        farmId,
        productionUnitId: productionUnitId || undefined,
        sourceEventType: 'feedLog'
      })
      .then((events) => {
        if (cancelled) return
        const feedLogIds = new Set(feedEventsAll.map((log) => log.id))
        const next = new Map<string, LotCostEvent>()
        for (const event of events) {
          if (event.sourceEventId && feedLogIds.has(event.sourceEventId)) {
            next.set(event.sourceEventId, event)
          }
        }
        setCostsByFeedLogId(next)
      })
      .catch(() => {
        if (cancelled) return
        setCostsByFeedLogId(new Map())
      })
    return () => {
      cancelled = true
    }
  }, [farmId, productionUnitId, feedEventsAll])

  const refreshCurrentFeedSyncStatus = useCallback(async (): Promise<EntityBatchSyncStatus | null> => {
    if (!farmId || !productionUnitId) {
      setFeedSyncStatus(null)
      return null
    }

    try {
      const localLogs = await feedLogApi.list({ farmId, productionUnitId }, { preferLocal: true })
      const feedLogIds = Array.from(new Set(localLogs.map((log) => log.id).filter(Boolean)))
      if (feedLogIds.length === 0) {
        const emptyStatus: EntityBatchSyncStatus = {
          syncState: 'synced',
          blockedCount: 0,
          errorMessage: null,
          blockers: []
        }
        setFeedSyncStatus(emptyStatus)
        return emptyStatus
      }

      const status = await feedLogApi.readFeedLogSyncStatus(feedLogIds)
      setFeedSyncStatus(status)
      return status
    } catch {
      setFeedSyncStatus(null)
      return null
    }
  }, [farmId, productionUnitId])

  // v2.1 finding #61 (WSL-09): keep the post-drain refresh ref pointed at
  // the latest `refreshCurrentFeedSyncStatus` identity so
  // `applyFeedEventStatus` (declared below) can call through it from its
  // `syncPromise.then` callback. See the ref's declaration block above for
  // the full rationale.
  useEffect(() => {
    refreshFeedSyncStatusFnRef.current = refreshCurrentFeedSyncStatus
  }, [refreshCurrentFeedSyncStatus])

  const countPendingFeedLogChanges = useCallback(async (): Promise<number> => {
    try {
      return await getOfflineDb().entityOutbox.where('entityType').equals('feedLog').count()
    } catch {
      return 0
    }
  }, [])

  const retryPendingFeedSync = useCallback(async (): Promise<void> => {
    const action = pendingFeedSyncActionRef.current
    const pendingBefore = await countPendingFeedLogChanges()
    if (!action && pendingBefore <= 0) return
    if (typeof navigator !== 'undefined' && !navigator.onLine) return

    if (pendingBefore > 0) {
      try {
        await drainEntityOutboxNow({ maxEntries: 100, maxPasses: 5 })
      } catch {
        // best-effort; we still refresh local/server state below.
      }
    }

    const remainingFeedChanges = await countPendingFeedLogChanges()
    if (action) {
      if (remainingFeedChanges <= 0) {
        pendingFeedSyncActionRef.current = null
        setFeedEventStatusMessage(t(getFeedEventStatusKey(action, 'synced')))
      } else {
        setFeedEventStatusMessage(t(getFeedEventStatusKey(action, 'pending')))
      }
    }

    await refreshCurrentFeedEvents({ forceRemote: true })
    await refreshCurrentFeedSyncStatus()
  }, [countPendingFeedLogChanges, refreshCurrentFeedEvents, refreshCurrentFeedSyncStatus, t])

  const applyFeedEventStatus = (
    verb: 'saved' | 'deleted',
    result: Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'>
  ) => {
    pendingFeedSyncActionRef.current = result.syncState === 'offline' ? verb : null
    const nextVersion = feedEventStatusVersionRef.current + 1
    feedEventStatusVersionRef.current = nextVersion
    setFeedEventStatusMessage(t(getFeedEventStatusKey(verb, result.syncState)))

    if (!result.syncPromise) return

    void result.syncPromise.then((nextState) => {
      if (feedEventStatusVersionRef.current !== nextVersion) return

      // v2.1 finding #61 (WSL-09): reconcile the attention-banner snapshot
      // so it flips from 'pending' to 'synced' immediately after the drain.
      // Placed BEFORE the `nextState === result.syncState` early-return so
      // the outbox refresh always fires — mutation-state parity is
      // orthogonal to outbox-drain progress. Without this, finding #58's
      // `showActionMessage` rule keeps the stale attention banner pinned
      // while suppressing the post-save toast — operator-visible regression
      // is "save appears stuck".
      void refreshFeedSyncStatusFnRef.current()

      if (nextState === result.syncState) return
      pendingFeedSyncActionRef.current = nextState === 'synced' ? null : verb
      setFeedEventStatusMessage(t(getFeedEventStatusKey(verb, nextState)))
    })
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    const media = window.matchMedia('(max-width: 430px)')
    const onChange = () => setIsSmallScreen(media.matches)

    onChange()

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange)
      return () => media.removeEventListener('change', onChange)
    }

    // Safari iOS < 14
    // eslint-disable-next-line deprecation/deprecation
    media.addListener(onChange)
    // eslint-disable-next-line deprecation/deprecation
    return () => media.removeListener(onChange)
  }, [])

  useEffect(() => {
    if (isSmallScreen) return

    if (panelMode === 'kpis') {
      setDesktopLogVisible(true)
    } else {
      setDesktopLogVisible(false)
    }
  }, [isSmallScreen, panelMode])

  useEffect(() => {
    setVisibleKpiWeeks(12)
  }, [farmId, productionUnitId])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      await refreshCurrentFeedEvents()
      await refreshCurrentFeedSyncStatus()
      if (cancelled) return
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [refreshCurrentFeedEvents, refreshCurrentFeedSyncStatus])

  useEffect(() => {
    const onOnline = () => {
      void retryPendingFeedSync()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      void retryPendingFeedSync()
    }

    const onFocus = () => {
      void retryPendingFeedSync()
    }

    globalThis.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibilityChange)
    globalThis.addEventListener('focus', onFocus)

    return () => {
      globalThis.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      globalThis.removeEventListener('focus', onFocus)
    }
  }, [retryPendingFeedSync])

  useEffect(() => {
    if (!farmId || !productionUnitId) return
    // Findings #173: after switching unit the draft could keep the previous
    // unit's lot — the select then showed this unit's lot while saving looked
    // up the old one ("Selecciona un lote activo…"). Keep the draft only when
    // it is still an active lot of this unit.
    if (feedEventDraft.lot && interactiveLots.some((lot) => lot.lotKey === feedEventDraft.lot)) return
    if (interactiveLots.length === 0) {
      if (feedEventDraft.lot) setFeedEventDraft((prev) => ({ ...prev, lot: '' }))
      return
    }

    setFeedEventDraft((prev) => ({ ...prev, lot: interactiveLots[0]?.lotKey ?? '' }))
  }, [interactiveLots, farmId, productionUnitId, feedEventDraft.lot])

  const deepLinkedFarmIdRef = useRef<string>('')

  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const [highlightCardKey, setHighlightCardKey] = useState<string>('')

  const [feedTypeSuggestions, setFeedTypeSuggestions] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false

    const loadFeedTypeSuggestions = async () => {
      setFeedTypeSuggestions([])
      const scopedFarmId = farmId?.trim()
      if (!scopedFarmId) return

      const fromRecordedAt = new Date(Date.now() - FEED_TYPE_SUGGESTION_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString()

      try {
        // Facility-scoped: suggests types used anywhere in the facility.
        const logs = await feedLogApi.list({ farmId: scopedFarmId, fromRecordedAt })
        if (cancelled) return

        const seen = new Map<string, string>()
        for (const log of logs) {
          const raw = typeof log.feedType === 'string' ? log.feedType.trim() : ''
          if (!raw) continue
          const key = raw.toLowerCase()
          if (!seen.has(key)) {
            seen.set(key, raw)
          }
        }

        const uniqueSorted = Array.from(seen.values())
          .sort((a, b) => a.localeCompare(b))
          .slice(0, 100)

        setFeedTypeSuggestions(uniqueSorted)
      } catch {
        if (cancelled) return
        setFeedTypeSuggestions([])
      }
    }

    void loadFeedTypeSuggestions()
    return () => {
      cancelled = true
    }
  }, [farmId])

  const parseWeekKey = (value: string): { year: number; week: number } | null => {
    const match = /^([0-9]{4})-W([0-9]{2})$/.exec(value)
    if (!match) return null
    const year = Number(match[1])
    const week = Number(match[2])
    if (!Number.isFinite(year) || !Number.isFinite(week)) return null
    return { year, week }
  }

  const toWeekOrdinal = (value: string): number => {
    const parsed = parseWeekKey(value)
    if (!parsed) return Number.NEGATIVE_INFINITY
    return parsed.year * 100 + parsed.week
  }

  const sortedFeedKPIs = useMemo(() => {
    return [...feedKPIs].sort((a, b) => {
      const aOrdinal = toWeekOrdinal(a.week)
      const bOrdinal = toWeekOrdinal(b.week)
      if (aOrdinal !== bOrdinal) return bOrdinal - aOrdinal
      return String(a.lot).localeCompare(String(b.lot))
    })
  }, [feedKPIs])

  const visibleSortedFeedKPIs = useMemo(() => {
    return sortedFeedKPIs.slice(0, visibleKpiWeeks)
  }, [sortedFeedKPIs, visibleKpiWeeks])

  const showSplitKpiCards = !isSmallScreen && panelMode === 'kpis'
  const desktopTopKpiCards = useMemo(() => {
    if (!showSplitKpiCards) return []
    return visibleSortedFeedKPIs.slice(0, 2)
  }, [showSplitKpiCards, visibleSortedFeedKPIs])

  const desktopBelowKpiCards = useMemo(() => {
    if (!showSplitKpiCards) return []
    return visibleSortedFeedKPIs.slice(2)
  }, [showSplitKpiCards, visibleSortedFeedKPIs])

  const formatKpiSourceDate = (value?: string) => {
    if (!value) return '—'

    // v2.1 finding #89: calendar-day sentinel (YYYY-MM-DDT00:00:00Z, .000Z
    // variant) is produced by CSV date-only imports (parseSlashDate →
    // normalizeToIsoZ) and by the Datatable "Add row" default. Rendering the
    // sentinel via `Intl.DateTimeFormat({ timeZone })` TZ-shifts UTC midnight
    // to the previous local day for behind-UTC farms (e.g. Chile UTC-4:
    // `17/12/2025` displays as `Dec 16`). Delegate the sentinel to the shared
    // `formatStoredDateTimeAsLocal` helper (v2.1 finding #49) so Feed matches
    // the Sampling / Database rendering contract. Real instants continue to
    // render through the farm-TZ formatter below. Underlying architectural
    // cleanup is TIME-01/02/03 in v2.2 (docs/time-normalization-plan.md).
    if (/^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000)?Z$/.test(value.trim())) {
      return formatStoredDateTimeAsLocal(value)
    }

    const parsed = Date.parse(value)
    if (!Number.isFinite(parsed)) return value

    return new Intl.DateTimeFormat(undefined, {
      timeZone: activeTimeZone,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(parsed))
  }

  const formatKpiSourceWeight = (value?: number) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
    return value.toLocaleString(undefined, {
      minimumFractionDigits: value < 1 ? 4 : 2,
      maximumFractionDigits: value < 1 ? 4 : 2
    })
  }

  const formatKpiSourceTemperature = (value?: number) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
    return value.toLocaleString(undefined, {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
      maximumFractionDigits: 1
    })
  }

  const renderKpiCard = (kpi: FeedKPI) => {
    const cardKey = `${kpi.lot}-${kpi.week}`
    const highlighted = highlightCardKey === cardKey

    return (
      <div
        key={cardKey}
        ref={(node) => {
          if (node) cardRefs.current.set(cardKey, node)
          else cardRefs.current.delete(cardKey)
        }}
        style={{
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: highlighted ? '2px solid #3b82f6' : '2px solid #e5e7eb',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4 style={{ margin: 0, color: '#374151' }}>{kpi.lot}</h4>
          <span
            style={{
              padding: '4px 8px',
              backgroundColor: getQualityColor(kpi.dataQuality.completeness),
              color: 'white',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            {t(getQualityTextKey(kpi.dataQuality.completeness))}
          </span>
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '10px' }}>
          {t('feed.kpi.weekLabel')} {kpi.weekDisplay}
        </div>

        {kpi.dataQuality.completeness >= 100 ? (
          <>
            {kpi.temperatureWarning && (
              <div
                style={{
                  marginBottom: '12px',
                  padding: '10px',
                  backgroundColor: kpi.temperatureStatus?.includes('critical') ? '#fef2f2' : '#fef3c7',
                  borderRadius: '6px',
                  border: `1px solid ${kpi.temperatureStatus?.includes('critical') ? '#fca5a5' : '#fbbf24'}`
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: kpi.temperatureStatus?.includes('critical') ? '#dc2626' : '#d97706',
                    marginBottom: '4px'
                  }}
                >
                  {t('feed.temperatureAlert.title')}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: kpi.temperatureStatus?.includes('critical') ? '#dc2626' : '#d97706'
                  }}
                >
                  {kpi.temperatureWarningKey
                    ? t(kpi.temperatureWarningKey, kpi.temperatureWarningParams)
                    : kpi.temperatureWarning}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.kpi.biomass')}{' '}</span>
              <span style={{ color: '#059669' }}>{kpi.biomassKg.toFixed(biomassDecimals)} kg</span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.kpi.sfr')}{' '}</span>
              <span style={{ color: '#0284c7' }}>
                {kpi.sfrPercent.toFixed(2)}%
                {kpi.clampedTemp && (
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                    {' '}(at {kpi.clampedTemp}°C)
                  </span>
                )}
              </span>
            </div>
            <div
              style={{
                marginBottom: '12px',
                padding: '10px 12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '12px',
                color: '#4b5563'
              }}
            >
              <div style={{ fontWeight: 700, color: '#374151', marginBottom: '4px' }}>SFR source inputs</div>
              <div>
                Weight: {formatKpiSourceWeight(kpi.sourceWeightG)} g on {formatKpiSourceDate(kpi.sourceWeightDate)}
              </div>
              <div>
                Temperature: {formatKpiSourceTemperature(kpi.sourceTemperatureC)}°C on {formatKpiSourceDate(kpi.sourceTemperatureDate)}
              </div>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontWeight: 'bold', color: '#374151' }}>
                {viewMode === 'daily' ? `${t('feed.kpi.dailyFeed')} ` : `${t('feed.kpi.weeklyFeed')} `}
              </span>
              <span style={{ color: '#dc2626', fontSize: '16px', fontWeight: 'bold' }}>
                {viewMode === 'daily' ? kpi.dailyFeedKg.toFixed(2) : kpi.weeklyFeedKg.toFixed(2)} kg
              </span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontWeight: 'bold', color: '#374151' }}>
                {t('feed.kpi.actualFeed')}{' '}
              </span>
              <span style={{ color: '#7c3aed', fontSize: '16px', fontWeight: 'bold' }}>
                {(viewMode === 'daily' ? kpi.actualDailyFeedKg : kpi.actualWeeklyFeedKg).toFixed(2)} kg
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                {' '}({kpi.actualEventCount} {t('feed.kpi.eventsSuffix')})
              </span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.kpi.adherence')}{' '}</span>
              <span style={{ color: '#374151' }}>
                {(() => {
                  const recommended = viewMode === 'daily' ? kpi.dailyFeedKg : kpi.weeklyFeedKg
                  const actual = viewMode === 'daily' ? kpi.actualDailyFeedKg : kpi.actualWeeklyFeedKg
                  if (recommended <= 0) return '—'
                  return `${Math.round((actual / recommended) * 100)}%`
                })()}
              </span>
            </div>
            {kpi.sgr && (
              <div style={{ marginBottom: '8px' }}>
                <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.kpi.sgr')}{' '}</span>
                <span style={{ color: '#7c3aed' }}>{kpi.sgr.toFixed(2)}%/day</span>
              </div>
            )}
            {kpi.fcr && (
              <div style={{ marginBottom: '8px' }} title={t('feed.kpi.fcrPlannedTooltip')}>
                <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.kpi.fcrPlanned')}{' '}</span>
                <span style={{ color: '#ea580c' }}>{kpi.fcr.toFixed(2)}</span>
                <span style={{ marginLeft: 4, color: '#9ca3af', fontSize: 12, cursor: 'help' }} aria-hidden>ⓘ</span>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '15px', backgroundColor: '#fee2e2', borderRadius: '6px' }}>
            <h5 style={{ margin: '0 0 8px 0', color: '#dc2626' }}>{t('feed.missingData.title')}</h5>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#dc2626', fontSize: '14px' }}>
              {kpi.dataQuality.missingData.map((missing, idx) => (
                <li key={idx}>{t(missing)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  const renderLoadOlderWeeks = () => {
    if (sortedFeedKPIs.length <= visibleKpiWeeks) return null

    return (
      <div style={{ marginBottom: 30 }}>
        <button
          type="button"
          onClick={() => setVisibleKpiWeeks((prev) => Math.min(sortedFeedKPIs.length, prev + 12))}
          style={{
            padding: '10px 14px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          {t('feed.button.loadOlderWeeks')}
        </button>
      </div>
    )
  }

  const renderSummaryStats = () => {
    return (
      <div
        style={{
          padding: '20px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}
      >
        <h4 style={{ marginTop: 0, color: '#475569' }}>{t('feed.summaryStats.title')}</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.summaryStats.totalBiomass')}{' '}</span>
            <span style={{ color: '#059669' }}>{feedSummary.totalBiomassKg.toFixed(biomassDecimals)} kg</span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold', color: '#374151' }}>
              {viewMode === 'daily' ? `${t('feed.summaryStats.totalDailyFeed')} ` : `${t('feed.summaryStats.totalWeeklyFeed')} `}
            </span>
            <span style={{ color: '#dc2626' }}>
              {(viewMode === 'daily' ? feedSummary.totalDailyFeedKg : feedSummary.totalWeeklyFeedKg).toFixed(1)} kg
            </span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.summaryStats.totalActualFeed')}{' '}</span>
            <span style={{ color: '#7c3aed' }}>
              {(viewMode === 'daily' ? feedSummary.totalActualDailyFeedKg : feedSummary.totalActualWeeklyFeedKg).toFixed(1)} kg
            </span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.summaryStats.overallAdherence')}{' '}</span>
            <span style={{ color: '#374151' }}>
              {(() => {
                const recommended = viewMode === 'daily' ? feedSummary.totalDailyFeedKg : feedSummary.totalWeeklyFeedKg
                const actual = viewMode === 'daily' ? feedSummary.totalActualDailyFeedKg : feedSummary.totalActualWeeklyFeedKg
                if (recommended <= 0) return '—'
                return `${Math.round((actual / recommended) * 100)}%`
              })()}
            </span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.summaryStats.avgSfr')}{' '}</span>
            <span style={{ color: '#0284c7' }}>
              {(feedKPIs.length > 0 ? feedSummary.averageSfrPercent : 0).toFixed(2)}%
            </span>
          </div>
          {feedSummary.fcrCount > 0 && (
            <div>
              <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.summaryStats.avgFcr')}{' '}</span>
              <span style={{ color: '#ea580c' }}>{feedSummary.averageFcr.toFixed(fcrDecimals)}</span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                {' '}({feedSummary.fcrCount} {t('feed.summaryStats.periodsSuffix')})
              </span>
            </div>
          )}
          <div>
            <span style={{ fontWeight: 'bold', color: '#374151' }}>{t('feed.summaryStats.dataQuality')}{' '}</span>
            <span
              style={{
                color: getQualityColor(feedKPIs.length > 0 ? feedSummary.averageQualityPercent : 0)
              }}
            >
              {(feedKPIs.length > 0 ? feedSummary.averageQualityPercent : 0).toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
    )
  }

  const feedStatsByWeek = useMemo(() => {
    type WeekStats = {
      week: string
      weekDisplay: string
      totalFeedKg: number
      totalActualFeedKg: number
      totalActualEventCount: number
      totalBiomassKg: number
      totalLotsCount: number
      completeLotsCount: number
      sfrAvgPercent: number
      sfrWeightedAvgPercent: number
      fcrAvg: number | null
      fcrCount: number
      sgrAvg: number | null
      sgrCount: number
    }

    const totals = new Map<string, {
      week: string
      weekDisplay: string
      totalFeedKg: number
      totalActualFeedKg: number
      totalActualEventCount: number
      totalBiomassKg: number
      totalLotsCount: number
      completeLotsCount: number
      sfrSum: number
      sfrCount: number
      sfrBiomassWeightedSum: number
      sfrBiomassWeight: number
      fcrSum: number
      fcrCount: number
      sgrSum: number
      sgrCount: number
    }>()

    sortedFeedKPIs.forEach((kpi) => {
      const current = totals.get(kpi.week)
      const feedKg = viewMode === 'daily' ? kpi.dailyFeedKg : kpi.weeklyFeedKg
      const actualFeedKg = viewMode === 'daily' ? kpi.actualDailyFeedKg : kpi.actualWeeklyFeedKg
      const biomassKg = typeof kpi.biomassKg === 'number' && Number.isFinite(kpi.biomassKg) ? kpi.biomassKg : 0

      const next = current ?? {
        week: kpi.week,
        weekDisplay: kpi.weekDisplay,
        totalFeedKg: 0,
        totalActualFeedKg: 0,
        totalActualEventCount: 0,
        totalBiomassKg: 0,
        totalLotsCount: 0,
        completeLotsCount: 0,
        sfrSum: 0,
        sfrCount: 0,
        sfrBiomassWeightedSum: 0,
        sfrBiomassWeight: 0,
        fcrSum: 0,
        fcrCount: 0,
        sgrSum: 0,
        sgrCount: 0
      }

      next.totalFeedKg += feedKg
      next.totalActualFeedKg += actualFeedKg
      next.totalActualEventCount += kpi.actualEventCount
      next.totalBiomassKg += biomassKg
      next.totalLotsCount += 1

      const isComplete = kpi.dataQuality?.completeness >= 100
      if (isComplete) {
        next.completeLotsCount += 1

        if (typeof kpi.sfrPercent === 'number' && Number.isFinite(kpi.sfrPercent) && kpi.sfrPercent > 0) {
          next.sfrSum += kpi.sfrPercent
          next.sfrCount += 1
          if (biomassKg > 0) {
            next.sfrBiomassWeightedSum += kpi.sfrPercent * biomassKg
            next.sfrBiomassWeight += biomassKg
          }
        }

        if (typeof kpi.fcr === 'number' && Number.isFinite(kpi.fcr) && kpi.fcr > 0) {
          next.fcrSum += kpi.fcr
          next.fcrCount += 1
        }

        if (typeof kpi.sgr === 'number' && Number.isFinite(kpi.sgr)) {
          next.sgrSum += kpi.sgr
          next.sgrCount += 1
        }
      }

      if (!current) {
        totals.set(kpi.week, next)
      }
    })

    const rows: WeekStats[] = [...totals.values()].map((row) => {
      const sfrAvg = row.sfrCount > 0 ? row.sfrSum / row.sfrCount : 0
      const sfrWeighted = row.sfrBiomassWeight > 0 ? row.sfrBiomassWeightedSum / row.sfrBiomassWeight : sfrAvg
      const fcrAvg = row.fcrCount > 0 ? row.fcrSum / row.fcrCount : null
      const sgrAvg = row.sgrCount > 0 ? row.sgrSum / row.sgrCount : null
      return {
        week: row.week,
        weekDisplay: row.weekDisplay,
        totalFeedKg: row.totalFeedKg,
        totalActualFeedKg: row.totalActualFeedKg,
        totalActualEventCount: row.totalActualEventCount,
        totalBiomassKg: row.totalBiomassKg,
        totalLotsCount: row.totalLotsCount,
        completeLotsCount: row.completeLotsCount,
        sfrAvgPercent: sfrAvg,
        sfrWeightedAvgPercent: sfrWeighted,
        fcrAvg,
        fcrCount: row.fcrCount,
        sgrAvg,
        sgrCount: row.sgrCount
      }
    })

    return rows.sort((a, b) => toWeekOrdinal(b.week) - toWeekOrdinal(a.week))
  }, [sortedFeedKPIs, viewMode])

  useEffect(() => {
    let cancelled = false

    const loadFarms = async () => {
      const farms = await ensureFarmList()
      if (cancelled) return

      setAvailableFarms(farms)

      // Load previously selected farm and production unit (deep-link overrides saved selection).
      const savedFarmToken = readScopedString('feed-selected-farm')
      const savedProductionUnitToken = readScopedString('feed-selected-production-unit')

      const activeContext = loadActiveContextSelection()

      const farmToken = deepLink.farmId || deepLink.farmName || activeContext.farmId || savedFarmToken || ''
      const resolvedFarm = farmToken ? findFarmByIdOrName(farms, farmToken) : undefined
      const nextFarmId = String(resolvedFarm?.id ?? farms?.[0]?.id ?? '')
      setFarmId(nextFarmId)

      const isDeepLinkFarmSelection = Boolean(deepLink.farmId || deepLink.farmName) && Boolean(resolvedFarm?.id)
      deepLinkedFarmIdRef.current = isDeepLinkFarmSelection ? nextFarmId : ''

      const selectedFarm = resolvedFarm ?? farms.find((farm: any) => String(farm?.id ?? '') === nextFarmId)
      const allowDeepLinkUnit = isDeepLinkFarmSelection && deepLinkedFarmIdRef.current === nextFarmId
      const deepLinkedUnitToken = allowDeepLinkUnit ? (deepLink.productionUnitId || deepLink.productionUnitName || '') : ''
      const productionUnitToken = deepLinkedUnitToken || activeContext.productionUnitId || savedProductionUnitToken || ''
      const resolvedProductionUnit = selectedFarm && productionUnitToken
        ? findProductionUnitByIdOrName(selectedFarm, productionUnitToken)
        : undefined
      setProductionUnitId(String(resolvedProductionUnit?.id ?? ''))
    }

    void loadFarms()

    return () => {
      cancelled = true
    }
  }, [deepLink.farmId, deepLink.farmName, deepLink.productionUnitId, deepLink.productionUnitName])

  useEffect(() => {
    if (!farmId) {
      setAvailableProductionUnits([])
      setProductionUnitId('')
      return
    }

    const selectedFarm = findFarmByIdOrName(availableFarms, farmId)
    if (selectedFarm) {
      const units = selectedFarm.productionUnits || []
      setAvailableProductionUnits(units)

      if (units.length === 0) {
        setProductionUnitId('')
      } else {
        const allowDeepLinkUnit = Boolean(deepLinkedFarmIdRef.current) && deepLinkedFarmIdRef.current === farmId
        const deepLinkedToken = allowDeepLinkUnit ? (deepLink.productionUnitId || deepLink.productionUnitName || '') : ''
        const deepLinkedUnit = deepLinkedToken ? findProductionUnitByIdOrName(selectedFarm, deepLinkedToken) : undefined

        const currentIsValid = Boolean(productionUnitId) && units.some((unit: any) => String(unit?.id ?? '') === productionUnitId)
        if (!currentIsValid && deepLinkedUnit?.id) {
          setProductionUnitId(String(deepLinkedUnit.id))
        } else if (!currentIsValid) {
          setProductionUnitId(String(units[0].id ?? ''))
        }
      }
    } else {
      setAvailableProductionUnits([])
      setProductionUnitId('')
    }
    
    // Save farm selection to localStorage
    if (farmId) {
      writeScopedString('feed-selected-farm', farmId)
      persistActiveContextSelection({ farmId })
    }
  }, [deepLink.productionUnitId, deepLink.productionUnitName, farmId, productionUnitId, availableFarms])

  useEffect(() => {
    if (deepLink.focusWeek !== 'closest') return
    if (feedKPIs.length === 0) return

    const todayWeek = getWeekNumberInTimeZone(new Date().toISOString(), activeTimeZone)
    const today = parseWeekKey(todayWeek)
    if (!today) return

    const toOrdinal = (parts: { year: number; week: number }): number => parts.year * 100 + parts.week
    const todayOrdinal = toOrdinal(today)

    let bestKey = ''
    let bestDistance = Number.POSITIVE_INFINITY
    let bestOrdinal = Number.NEGATIVE_INFINITY

    feedKPIs.forEach((kpi) => {
      const parsed = parseWeekKey(kpi.week)
      if (!parsed) return
      const ordinal = toOrdinal(parsed)
      const distance = Math.abs(ordinal - todayOrdinal)
      const cardKey = `${kpi.lot}-${kpi.week}`

      if (distance < bestDistance || (distance === bestDistance && ordinal > bestOrdinal)) {
        bestDistance = distance
        bestOrdinal = ordinal
        bestKey = cardKey
      }
    })

    if (!bestKey) return
    const node = cardRefs.current.get(bestKey)
    if (!node) return

    node.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlightCardKey(bestKey)
    window.setTimeout(() => {
      setHighlightCardKey((prev) => (prev === bestKey ? '' : prev))
    }, 2800)
  }, [deepLink.focusWeek, feedKPIs, activeTimeZone])

  // Save production unit selection
  useEffect(() => {
    if (productionUnitId) {
      writeScopedString('feed-selected-production-unit', productionUnitId)
      persistActiveContextSelection({ productionUnitId })
    }
  }, [productionUnitId])

  useEffect(() => {
    if (!farmId || !productionUnitId) return
    const selectedFarm = findFarmByIdOrName(availableFarms, farmId)
    const selectedProductionUnit = selectedFarm ? findProductionUnitByIdOrName(selectedFarm, productionUnitId) : undefined
    if (!selectedFarm || !selectedProductionUnit) return

    const context: ProductionUnitTableContext = {
      farmId: String(selectedFarm.id ?? ''),
      productionUnitId: String(selectedProductionUnit.id ?? ''),
      farmName: selectedFarm.name,
      productionUnitName: selectedProductionUnit.name ?? undefined
    }

    let cancelled = false
    const canRefresh = Boolean(selectedFarm.id) && Boolean(selectedProductionUnit.id)
    if (canRefresh) {
      void refreshProductionUnitTableFromApi(context)
        .then(() => {
          if (!cancelled) setDataRevision((value) => value + 1)
        })
        .catch(() => {})

      void refreshWaterTableRowsFromApi({
        farmId: String(selectedFarm.id),
        productionUnitId: String(selectedProductionUnit.id),
        farmName: selectedFarm.name,
        productionUnitName: selectedProductionUnit.name ?? undefined
      })
        .then(() => {
          if (!cancelled) setDataRevision((value) => value + 1)
        })
        .catch(() => {})
    }

    return () => {
      cancelled = true
    }
  }, [farmId, productionUnitId, availableFarms, activeTimeZone])

  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setProductionRows([])
      return
    }

    const selectedFarm = findFarmByIdOrName(availableFarms, farmId)
    const selectedProductionUnit = selectedFarm ? findProductionUnitByIdOrName(selectedFarm, productionUnitId) : undefined
    if (!selectedFarm || !selectedProductionUnit) {
      setProductionRows([])
      return
    }

    const context: ProductionUnitTableContext = {
      farmId: String(selectedFarm.id ?? ''),
      productionUnitId: String(selectedProductionUnit.id ?? ''),
      farmName: selectedFarm.name,
      productionUnitName: selectedProductionUnit.name ?? undefined
    }

    let cancelled = false
    void (async () => {
      try {
        const rows = await listProductionUnitTableRows(context)
        if (!cancelled) setProductionRows(rows)
      } catch {
        if (!cancelled) setProductionRows([])
      }
    })()

    return () => {
      cancelled = true
    }
  }, [farmId, productionUnitId, availableFarms, dataRevision])

  // v2.1 #32 — load mortality logs alongside production rows so the Feed KPI
  // gate sees the same canonical mortality that Biomass / Database overlay.
  // Mirrors `refreshCurrentMortalityLogs` in Biomass.tsx; reuses the same
  // `mortalityLogApi.list` boundary, so offline-first semantics are
  // preserved (preferLocal: true to avoid spurious resets on transient
  // network failures).
  //
  // Post-T3-A: also load WeightSampleLog + FishStockLog for the parallel
  // overlays (same offline-first contract). Without these two loaders the
  // Feed KPI cards read blank `weight` / `fishStock` cells and report
  // "Datos faltantes" even after a successful CSV import — the import
  // pipeline synthesizes the events but Feed never overlays them.
  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setMortalityLogs([])
      setWeightSampleLogs([])
      setFishStockLogs([])
      return
    }

    const selectedFarm = findFarmByIdOrName(availableFarms, farmId)
    const selectedProductionUnit = selectedFarm ? findProductionUnitByIdOrName(selectedFarm, productionUnitId) : undefined
    if (!selectedFarm || !selectedProductionUnit) {
      setMortalityLogs([])
      setWeightSampleLogs([])
      setFishStockLogs([])
      return
    }

    const listContext = {
      farmId: String(selectedFarm.id ?? ''),
      productionUnitId: String(selectedProductionUnit.id ?? '')
    }

    let cancelled = false
    void (async () => {
      try {
        const [mortLogs, weightLogs, stockLogs] = await Promise.all([
          mortalityLogApi.list(listContext, { preferLocal: true }),
          weightSampleLogApi.list(listContext, { preferLocal: true }),
          fishStockLogApi.list(listContext, { preferLocal: true })
        ])
        if (cancelled) return
        setMortalityLogs(mortLogs)
        setWeightSampleLogs(weightLogs)
        setFishStockLogs(stockLogs)
      } catch {
        // preferLocal already returns local rows on transient failures;
        // reaching this branch means the local read itself threw, so it
        // is safe to fall back to an empty list rather than keep stale
        // events for a different (farm, PU) selection.
        if (!cancelled) {
          setMortalityLogs([])
          setWeightSampleLogs([])
          setFishStockLogs([])
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [farmId, productionUnitId, availableFarms, dataRevision])

  // v2.1 #32 + T3-A: overlay MortalityLog / WeightSampleLog / FishStockLog
  // events onto the raw production rows so downstream derivation
  // (end-of-day stock, KPI gate, biomass projection) sees the canonical
  // post-Tier-A values on the row itself. Chain order mirrors
  // `samplingRows` in Biomass.tsx: mortality → weight → fishStock, so
  // `row.dailyMortality` / `row.weight` / `row.fishStock` all resolve to
  // the latest authoritative event when one exists for `(lot, calendar
  // day)`. Without any of these overlays the KPI cards tag imported
  // rows as "Datos faltantes" even though Database renders them.
  const productionRowsWithOverlays = useMemo(
    () => overlayFishStockOnProductionRows(
      overlayWeightSamplesOnProductionRows(
        overlayMortalityLogsOnProductionRows(
          dropEmptySameDayDuplicateRows(productionRows).filter((row) => !isDeletedLotRow(row, productionUnitId)),
          mortalityLogs
        ),
        weightSampleLogs
      ),
      fishStockLogs
    ),
    [productionRows, mortalityLogs, weightSampleLogs, fishStockLogs, isDeletedLotRow, productionUnitId]
  )

  // Load and calculate KPIs
  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setFeedKPIs([])
      return
    }

    const calculateKPIs = () => {
      const parseKg = (value: unknown): number => {
        if (value === null || value === undefined) return 0
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0
        if (typeof value !== 'string') return 0
        const trimmed = value.trim()
        if (!trimmed) return 0
        const normalized = trimmed.replace(',', '.')
        const n = Number(normalized)
        if (!Number.isFinite(n)) return 0
        return n
      }

      // Use Dexie-backed farm list already loaded into state.
      const farms = availableFarms
      
      // Find farm by ID
      const selectedFarm = findFarmByIdOrName(farms, farmId)
      if (!selectedFarm) {
        setFeedKPIs([])
        return
      }
      
      // Find production unit by ID
      const productionUnits = selectedFarm.productionUnits || []
      const selectedProductionUnit = findProductionUnitByIdOrName(selectedFarm, productionUnitId)
      if (!selectedProductionUnit) {
        setFeedKPIs([])
        return
      }

      const tableContext: ProductionUnitTableContext = {
        farmId: String(selectedFarm.id ?? ''),
        productionUnitId: String(selectedProductionUnit.id ?? ''),
        farmName: selectedFarm.name,
        productionUnitName: selectedProductionUnit.name ?? undefined
      }

      const { canonicalLotIdByToken, canonicalWeightByToken } = buildFeedCanonicalLotMaps({
        availableLots,
        lotLineageRows
      })

      const resolveWeeklyBiomassKg = buildFeedWeeklyBiomassResolver({
        productionRows: [...productionRows, ...crossFarmSupplement.extraProductionRows],
        mortalityLogs: [...mortalityLogs, ...crossFarmSupplement.extraMortalityLogs],
        weightSampleLogs: [...weightSampleLogs, ...crossFarmSupplement.extraWeightSampleLogs],
        fishStockLogs: [...fishStockLogs, ...crossFarmSupplement.extraFishStockLogs],
        canonicalLotIdByToken,
        canonicalWeightByToken
      })

      const dbData = productionRowsWithOverlays as unknown as (DatabaseRow | BiomassDataRow)[]
      // v2.1 #18 + #32 + T3-A — derive end-of-day stock once so the KPI
      // fetcher can prefer the derived value (mortality-aware, MORT-12
      // friendly) when the stored `fishStock` cell is blank. Mirrors the
      // same fix landed in Biomass `buildBasicKpis` (#9) and the CSV export
      // (#15). Critical: feed the *overlaid* rows (mortality + weight +
      // fishStock) so the derivation chain sees the canonical Tier A values
      // instead of the (now-retired) `ProductionRecord.fishStock` cache.
      const derivedStockByRowId = computeDisplayedEndOfDayStockByRowId(productionRowsWithOverlays)
      
      // Load water data (local-first via boundary)
      const waterContext = {
        farmId: String(selectedFarm.id ?? ''),
        productionUnitId: String(selectedProductionUnit.id ?? ''),
        farmName: selectedFarm.name,
        productionUnitName: selectedProductionUnit.name ?? undefined
      }
      const waterData: WaterDataRow[] = loadWaterTableRows(waterContext)

      // Quick diagnostic - check if we have basic data
      if (dbData.length === 0) {
        console.warn('⚠️ No database records found. Check if farm/production unit is correct.')
        setFeedKPIs([])
        return
      }
      if (waterData.length === 0) {
        console.warn('⚠️ No water records found. This will cause missing temperature warnings.')
      }

      // Group data by lot and week
      const kpiMap = new Map<string, FeedKPI>()
      const dbRowsByCanonicalWeek = new Map<string, Array<DatabaseRow | BiomassDataRow>>()
      const dbRowByCanonicalWeek = new Map<string, DatabaseRow | BiomassDataRow>()
      const canonicalWeekAliasToPrimary: FeedAliasIndex = new Map()
      const waterRowsByCanonicalWeek = new Map<string, WaterDataRow[]>()

      // Process database rows
      dbData.forEach(dbRow => {
        const lotIdCandidate = (dbRow as any).lotId
        const lotLabel = String(dbRow.lot ?? '').trim()
        if (!dbRow.date) return

        const lotCanonical = resolveFeedLotToken({
          lotId: lotIdCandidate,
          lot: lotLabel
        })
        if (!lotCanonical) return
        
        const week = getWeekNumberInTimeZone(dbRow.date, activeTimeZone)
        const key = `${lotCanonical}-${week}`

        const aliasCandidates = [
          resolveFeedLotToken({ lotId: lotIdCandidate }),
          resolveFeedLotToken({ lot: lotLabel })
        ].filter((candidate) => Boolean(candidate && candidate.trim() !== '')) as string[]

        aliasCandidates.forEach((alias) => {
          addFeedAliasMapping({
            aliasIndex: canonicalWeekAliasToPrimary,
            aliasWeekKey: `${alias}-${week}`,
            primaryKey: key
          })
        })

        const rows = dbRowsByCanonicalWeek.get(key)
        if (rows) rows.push(dbRow)
        else dbRowsByCanonicalWeek.set(key, [dbRow])
      })

      dbRowsByCanonicalWeek.forEach((rows, key) => {
        const dbRow = selectLatestDatedRow(rows)
        if (!dbRow) return

        dbRowByCanonicalWeek.set(key, dbRow)

        const sampleRow = selectLatestDatedRow(rows.filter((row) => (parseFloat(row.weight) || 0) > 0))

        const lotId = typeof (dbRow as { lotId?: string }).lotId === 'string'
          ? String((dbRow as { lotId?: string }).lotId ?? '').trim()
          : ''
        const lotCanonical = resolveFeedLotToken({ lotId, lot: dbRow.lot })
        const lotLabel = String(dbRow.lot ?? '').trim()
        const week = getWeekNumberInTimeZone(dbRow.date, activeTimeZone)

        const kpi: FeedKPI = {
          lot: lotLabel,
          week,
          weekDisplay: formatWeekDisplay(dbRow.date, week, activeTimeZone),
          biomassKg: 0,
          sfrPercent: 0,
          dailyFeedKg: 0,
          weeklyFeedKg: 0,
          actualDailyFeedKg: 0,
          actualWeeklyFeedKg: 0,
          actualEventCount: 0,
          dataQuality: {
            hasWeight: false,
            hasStock: false,
            hasTemperature: false,
            completeness: 0,
            missingData: []
          }
        }

        if (sampleRow) {
          kpi.sampleWeightG = parseFloat(sampleRow.weight) || 0
          kpi.sampleDate = getZonedIsoDate(String(sampleRow.date ?? ''), activeTimeZone) ?? undefined
        }

        const weight = parseFloat(dbRow.weight) || 0
        const storedStock = parseFloat(dbRow.fishStock) || 0
        // v2.1 #18 — fall back to the derived end-of-day stock when the
        // stored cell is blank (post-MORT-12 the canonical stock often
        // lives only in the derivation), so the KPI doesn't report
        // "missing stock" against rows whose stock is computable.
        const derivedStock = derivedStockByRowId.get(String((dbRow as { id?: string }).id ?? ''))?.numericValue ?? null
        const stock = storedStock > 0 ? storedStock : (derivedStock ?? 0)
        const helperBiomassKg = resolveWeeklyBiomassKg({
          lotId,
          lotToken: lotCanonical,
          toRecordedAt: dbRow.date
        })

        // v2.1 #18 — split the gate so hasWeight and hasStock report
        // independently. Previously a single `weight > 0 && stock > 0`
        // guard set BOTH flags, causing rows with a real weight but a
        // blank stock to be tagged "missing weight" as well.
        if (weight > 0) {
          kpi.dataQuality.hasWeight = true
          kpi.sourceWeightG = weight
          kpi.sourceWeightDate = dbRow.date
        }
        if (stock > 0) {
          kpi.dataQuality.hasStock = true
        }
        if (helperBiomassKg !== null && helperBiomassKg > 0) {
          kpi.biomassKg = helperBiomassKg
        } else if (weight > 0 && stock > 0) {
          kpi.biomassKg = (stock * weight) / 1000
        }

        kpiMap.set(key, kpi)
      })

      // Process water data for temperature
      waterData.forEach(waterRow => {
        const lotIdCandidate = (waterRow as any).lotId
        const lotLabel = String((waterRow as any).lot ?? '').trim()
        if (!waterRow.date || waterRow.temperature === null || waterRow.temperature === undefined) return
        
        const week = getWeekNumberInTimeZone(waterRow.date, activeTimeZone)

        const tokenCandidates = [
          resolveFeedLotToken({ lotId: lotIdCandidate }),
          resolveFeedLotToken({ lot: lotLabel })
        ].filter((candidate) => Boolean(candidate && candidate.trim() !== '')) as string[]

        const primaryKey = resolveFeedPrimaryKeyForWaterRow({
          tokenCandidates,
          week,
          aliasIndex: canonicalWeekAliasToPrimary,
          hasPrimaryKey: (candidate) => kpiMap.has(candidate)
        })

        if (!primaryKey) return

        const rows = waterRowsByCanonicalWeek.get(primaryKey)
        if (rows) rows.push(waterRow)
        else waterRowsByCanonicalWeek.set(primaryKey, [waterRow])
      })

      kpiMap.forEach((kpi, primaryKey) => {
        const dbRow = dbRowByCanonicalWeek.get(primaryKey)
        if (!dbRow) return

        const waterRow = selectNearestDatedRow(String(dbRow.date ?? ''), waterRowsByCanonicalWeek.get(primaryKey) ?? [])
        if (!waterRow || waterRow.temperature === null || waterRow.temperature === undefined) return

        kpi.dataQuality.hasTemperature = true

        const weight = parseFloat(dbRow.weight) || 0
        if (weight <= 0) return

        const effectiveMatrix = resolveFeedSfrMatrix((dbRow as any).lotId, dbRow.lot)
        const sfrResult = interpolateFromMatrix(effectiveMatrix, weight, waterRow.temperature, { species: farmSpeciesKey })
        kpi.sfrPercent = sfrResult.sfr
        kpi.sourceWeightG = weight
        kpi.sourceWeightDate = dbRow.date
        kpi.sourceTemperatureC = waterRow.temperature
        kpi.sourceTemperatureDate = waterRow.date
        kpi.temperatureWarning = sfrResult.temperatureWarning
        kpi.temperatureWarningKey = sfrResult.temperatureWarningKey as TranslationKey | undefined
        kpi.temperatureWarningParams = sfrResult.temperatureWarningParams
        kpi.temperatureStatus = sfrResult.temperatureStatus
        kpi.clampedTemp = sfrResult.clampedTemp
        kpi.dailyFeedKg = (kpi.biomassKg * kpi.sfrPercent) / 100
        kpi.weeklyFeedKg = computeProjectedWeeklyFeedKg(kpi.biomassKg, kpi.sfrPercent)
      })

      // Calculate data completeness, SGR, and FCR
      const kpis = Array.from(kpiMap.values()).map(kpi => {
        // Track specific missing data
        kpi.dataQuality.missingData = []
        if (!kpi.dataQuality.hasWeight) kpi.dataQuality.missingData.push('feed.missingData.weight')
        if (!kpi.dataQuality.hasStock) {
          kpi.dataQuality.missingData.push(
            farmSpeciesKey === 'shrimp' ? 'feed.missingData.stockPl' : 'feed.missingData.stockFish'
          )
        }
        if (!kpi.dataQuality.hasTemperature) kpi.dataQuality.missingData.push('feed.missingData.temperature')
        
        const qualityScore = [
          kpi.dataQuality.hasWeight,
          kpi.dataQuality.hasStock, 
          kpi.dataQuality.hasTemperature
        ].filter(Boolean).length
        
        kpi.dataQuality.completeness = (qualityScore / 3) * 100

        return kpi
      })

      // Calculate per-lot SGR + FCR from historical periods.
      // Important: these must be computed within each lot time-series (not across lots).
      const byLot = new Map<string, FeedKPI[]>()
      kpis.forEach((kpi) => {
        const key = (kpi.lot ?? '').trim() || '(unknown lot)'
        const list = byLot.get(key)
        if (list) list.push(kpi)
        else byLot.set(key, [kpi])
      })

      const all: FeedKPI[] = []
      byLot.forEach((rows) => {
        const sorted = rows.slice().sort((a, b) => a.week.localeCompare(b.week))
        sorted.forEach((currentKpi, index) => {
          currentKpi.fcr = undefined
          currentKpi.sgr = undefined

          // SGR (%/day) — src/utils/lotGrowthFormulas.ts, shared with the
          // Reports `lotGrowthPerformance` dataset: mean weight of this
          // week's sample to the lot's next sample, real days between them
          // (findings #213).
          const nextSample = currentKpi.sampleWeightG
            ? sorted.slice(index + 1).find((kpi) => (kpi.sampleWeightG ?? 0) > 0)
            : undefined
          if (nextSample && currentKpi.sampleDate && nextSample.sampleDate) {
            currentKpi.sgr = computeSgrFromSamples({
              startWeightG: currentKpi.sampleWeightG ?? 0,
              startDate: currentKpi.sampleDate,
              endWeightG: nextSample.sampleWeightG ?? 0,
              endDate: nextSample.sampleDate
            })?.sgrPercent
          }

          if (index >= sorted.length - 1) return
          const nextKpi = sorted[index + 1]

          // FCR (planned): projected feed ÷ biomass gained to next week.

          if (currentKpi.biomassKg > 0 && nextKpi.biomassKg > 0) {
            const weightGainedKg = nextKpi.biomassKg - currentKpi.biomassKg
            currentKpi.fcr = computeFcrPlanned(currentKpi.weeklyFeedKg, weightGainedKg)
          }
        })

        all.push(...sorted)
      })

      // Actual fed (FeedLog events) totals, aligned to the same lot/week keys.
      assignActualFeedToKpis({
        feedEvents: currentFeedEventsAll,
        kpiByPrimaryKey: kpiMap,
        aliasIndex: canonicalWeekAliasToPrimary,
        weekOf: (recordedAt) => getWeekNumberInTimeZone(recordedAt, activeTimeZone),
        parseKg
      })

      setFeedKPIs(all.sort((a, b) => b.week.localeCompare(a.week)))
      
      // Helpful summary for user
      if (all.length > 0) {
        const completeData = all.filter(k => k.dataQuality.completeness === 100).length
        const partialData = all.filter(k => k.dataQuality.completeness >= 67 && k.dataQuality.completeness < 100).length
        const incompleteData = all.filter(k => k.dataQuality.completeness < 67).length
        
        // Feed Planning Summary: ${all.length} records found
        // Complete: ${completeData}, Partial: ${partialData}, Incomplete: ${incompleteData}
      }
    }

    calculateKPIs()
  }, [
    farmId,
    productionUnitId,
    availableFarms,
    availableLots,
    lotLineageRows,
    dataRevision,
    currentFeedEventsAll,
    activeTimeZone,
    productionRows,
    productionRowsWithOverlays,
    mortalityLogs,
    weightSampleLogs,
    fishStockLogs,
    crossFarmSupplement
  ])

  const availableWeeks = useMemo(
    () => [...new Set(feedKPIs.map((kpi) => kpi.week))].sort().reverse(),
    [feedKPIs]
  )

  const feedSummary = useMemo(() => {
    const count = feedKPIs.length
    if (count === 0) {
      return {
        totalBiomassKg: 0,
        totalDailyFeedKg: 0,
        totalWeeklyFeedKg: 0,
        totalActualDailyFeedKg: 0,
        totalActualWeeklyFeedKg: 0,
        averageSfrPercent: 0,
        averageQualityPercent: 0,
        averageFcr: 0,
        fcrCount: 0
      }
    }

    let totalBiomassKg = 0
    let totalDailyFeedKg = 0
    let totalWeeklyFeedKg = 0
    let totalActualDailyFeedKg = 0
    let totalActualWeeklyFeedKg = 0
    let totalSfrPercent = 0
    let totalQualityPercent = 0
    let totalFcr = 0
    let fcrCount = 0

    feedKPIs.forEach((kpi) => {
      totalBiomassKg += kpi.biomassKg
      totalDailyFeedKg += kpi.dailyFeedKg
      totalWeeklyFeedKg += kpi.weeklyFeedKg
      totalActualDailyFeedKg += kpi.actualDailyFeedKg
      totalActualWeeklyFeedKg += kpi.actualWeeklyFeedKg
      totalSfrPercent += kpi.sfrPercent
      totalQualityPercent += kpi.dataQuality.completeness

      if (typeof kpi.fcr === 'number' && Number.isFinite(kpi.fcr) && kpi.fcr > 0) {
        totalFcr += kpi.fcr
        fcrCount += 1
      }
    })

    return {
      totalBiomassKg,
      totalDailyFeedKg,
      totalWeeklyFeedKg,
      totalActualDailyFeedKg,
      totalActualWeeklyFeedKg,
      averageSfrPercent: totalSfrPercent / count,
      averageQualityPercent: totalQualityPercent / count,
      averageFcr: fcrCount > 0 ? totalFcr / fcrCount : 0,
      fcrCount
    }
  }, [feedKPIs])

  const getQualityColor = (completeness: number) => {
    if (completeness >= 100) return '#22c55e'
    if (completeness >= 67) return '#f59e0b' 
    return '#ef4444'
  }

  const getQualityTextKey = (completeness: number): TranslationKey => {
    if (completeness >= 100) return 'feed.quality.complete'
    if (completeness >= 67) return 'feed.quality.partial'
    return 'feed.quality.incomplete'
  }

  const selectedFarmLabel = useMemo(() => {
    if (!farmId) return '—'
    const selected = availableFarms.find((farm: any) => String(farm.id ?? farm.name ?? '') === String(farmId))
    return selected?.name ?? String(farmId)
  }, [availableFarms, farmId])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!productionUnitId) return '—'
    const selected = availableProductionUnits.find((unit: any) => String(unit.id ?? unit.name ?? '') === String(productionUnitId))
    return selected?.name ?? String(productionUnitId)
  }, [availableProductionUnits, productionUnitId])

  const feedSyncAttentionMessage = useMemo(() => {
    if (!feedSyncStatus || feedSyncStatus.syncState === 'synced') return null

    if (feedSyncStatus.blockedCount > 0) {
      return feedSyncStatus.errorMessage
        ? t('feed.sync.blockedWithReason', { count: feedSyncStatus.blockedCount, reason: feedSyncStatus.errorMessage })
        : t('feed.sync.blocked', { count: feedSyncStatus.blockedCount })
    }

    if (feedSyncStatus.syncState === 'offline') {
      return t('feed.sync.retryOffline')
    }

    return t('feed.sync.retryPending')
  }, [feedSyncStatus, t])

  const feedSyncLastResultSummary = useMemo(() => {
    if (!feedSyncLastOutcome) return null

    const resultMessage = t(feedSyncLastOutcome.resultKey, feedSyncLastOutcome.resultParams)
    const attemptedAtLabel = new Date(feedSyncLastOutcome.attemptedAtIso).toLocaleString()

    return {
      resultLabel: t('feed.sync.lastResult', { result: resultMessage }),
      attemptedAtLabel: t('feed.sync.lastAttempted', { time: attemptedAtLabel })
    }
  }, [feedSyncLastOutcome, t])

  const handleFeedSyncRetry = useCallback(async () => {
    if (!farmId || !productionUnitId) {
      setFeedSyncStatus(null)
      return
    }

    setRetryingFeedSync(true)
    setFeedEventsError(null)

    try {
      const localLogs = await feedLogApi.list({ farmId, productionUnitId }, { preferLocal: true })
      const feedLogIds = Array.from(new Set(localLogs.map((log) => log.id).filter(Boolean)))
      if (feedLogIds.length === 0) {
        setFeedSyncStatus({
          syncState: 'synced',
          blockedCount: 0,
          errorMessage: null,
          blockers: []
        })
        return
      }

      const retryReport = await feedLogApi.retryFeedLogSyncStatusWithReport(feedLogIds)
      const nextOutcome = deriveFeedSyncOutcome({
        status: retryReport.status,
        skippedNotFoundCount: retryReport.skippedNotFoundCount
      })

      setFeedSyncStatus(retryReport.status)
      setFeedSyncLastOutcome({
        ...nextOutcome,
        attemptedAtIso: new Date().toISOString()
      })
      setFeedEventStatusMessage(t(nextOutcome.resultKey, nextOutcome.resultParams))

      await refreshCurrentFeedEvents({ forceRemote: true })
      await refreshCurrentFeedSyncStatus()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('feed.sync.retryFailed')
      setFeedEventsError(message)
    } finally {
      setRetryingFeedSync(false)
    }
  }, [farmId, productionUnitId, refreshCurrentFeedEvents, refreshCurrentFeedSyncStatus, t])

  const handleManualRefresh = async () => {
    if (!farmId || !productionUnitId) return

    setFeedEventsError(null)
    setFeedEventStatusMessage(t('feed.status.refreshing'))

    const pendingBefore = await countPendingFeedLogChanges()
    if ((typeof navigator === 'undefined' || navigator.onLine) && pendingBefore > 0) {
      try {
        await drainEntityOutboxNow({ maxEntries: 100, maxPasses: 5 })
      } catch {
        // best-effort; status and refresh below still reflect the latest known state.
      }
    }

    const refreshed = await refreshCurrentFeedEvents({ forceRemote: true })
    const remainingFeedChanges = await countPendingFeedLogChanges()
    const action = pendingFeedSyncActionRef.current
    const nextSyncStatus = await refreshCurrentFeedSyncStatus()

    if (action && remainingFeedChanges <= 0) {
      pendingFeedSyncActionRef.current = null
      setFeedEventStatusMessage(t(getFeedEventStatusKey(action, 'synced')))
      return
    }

    if (action && remainingFeedChanges > 0) {
      setFeedEventStatusMessage(t(getFeedEventStatusKey(action, 'pending')))
      if (nextSyncStatus) {
        const outcome = deriveFeedSyncOutcome({
          status: nextSyncStatus,
          skippedNotFoundCount: 0
        })
        setFeedSyncLastOutcome({
          ...outcome,
          attemptedAtIso: new Date().toISOString()
        })
      }
      return
    }

    if (refreshed) {
      setFeedEventStatusMessage(t('feed.status.refreshed'))
      if (nextSyncStatus) {
        const outcome = deriveFeedSyncOutcome({
          status: nextSyncStatus,
          skippedNotFoundCount: 0
        })
        setFeedSyncLastOutcome({
          ...outcome,
          attemptedAtIso: new Date().toISOString()
        })
      }
    } else {
      setFeedEventStatusMessage(null)
    }
  }

  const showDesktopLogPanel = panelMode === 'kpis' ? true : desktopLogVisible
  const showLogPanel = isSmallScreen ? activePanel === 'log' : showDesktopLogPanel
  const showAnalyzePanel = isSmallScreen ? activePanel === 'analyze' : true

  return (
    <div className="responsive-container">
      <h2>{t('feed.title')}</h2>

      {/* findings #101: whole-facility table first; today's per-unit page unchanged behind "Por unidad". */}
      <PillTabs<FeedView>
        ariaLabel={t('feed.view.ariaLabel')}
        testId="feed-view-tabs"
        active={feedView}
        onChange={changeFeedView}
        tabs={[
          { key: 'farm', label: t('feed.view.farm'), testId: 'feed-view-farm' },
          { key: 'unit', label: t('feed.view.unit'), testId: 'feed-view-unit' }
        ]}
      />
      {feedView === 'farm' ? (
        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
          <label style={{ display: 'grid', gap: 4, maxWidth: 360, fontSize: 13 }}>
            <strong>{t('feed.context.facility')}</strong>
            <select
              value={farmId}
              onChange={(event) => setFarmId(event.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8 }}
              data-testid="feed-farm-view-facility"
            >
              {farmId ? null : <option value="">{t('feed.placeholder.selectFacility')}</option>}
              {availableFarms.map((farm: any) => (
                <option key={String(farm.id ?? farm.name ?? '')} value={String(farm.id ?? farm.name ?? '')}>{farm.name}</option>
              ))}
            </select>
          </label>
          {selectedFarm ? (
            <FarmFeedTable
              farm={selectedFarm}
              canEdit={canEditFeedEvents}
              onFarmSaved={(nextFarm) => setAvailableFarms((prev) => prev.map((farm: any) => (String(farm?.id) === String(nextFarm.id) ? nextFarm : farm)))}
            />
          ) : null}
        </div>
      ) : null}

      <div hidden={feedView === 'farm'}>
      {/* Farm and Production Unit Selection */}
      <CurrentContextBar
        fields={[
          { label: t('feed.context.facility'), value: selectedFarmLabel },
          { label: t('feed.context.productionUnit'), value: selectedProductionUnitLabel }
        ]}
      />
      <div className="responsive-toolbar">
        <ContextSelectorBar
          farm={{
            label: t('feed.context.facility'),
            value: farmId,
            onChange: (value) => setFarmId(value),
            placeholder: t('feed.placeholder.selectFacility'),
            options: availableFarms.map((farm: any) => ({
              key: String(farm.id ?? farm.name ?? ''),
              value: String(farm.id ?? farm.name ?? ''),
              label: farm.name
            }))
          }}
          productionUnit={{
            label: t('feed.context.productionUnit'),
            value: productionUnitId,
            onChange: (value) => setProductionUnitId(value),
            disabled: !farmId || availableProductionUnits.length === 0,
            placeholder: !farmId ? t('feed.placeholder.selectFacilityFirst') : t('feed.placeholder.selectProductionUnit'),
            options: buildUnitSelectOptions(availableProductionUnits, t('unit.inactive.suffix'))
          }}
          summary={farmId && productionUnitId ? (
            <div style={{
              padding: '10px 15px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d7ff',
              borderRadius: '6px',
              fontWeight: 'bold',
              color: '#0056b3',
              flex: '0 0 auto'
            }}>
              {t('feed.summary.calculationPeriods', { count: feedKPIs.length })}
            </div>
          ) : null}
        />
      </div>

      {importReviewNotice ? (
        <div style={{
          marginBottom: 16,
          padding: '10px 14px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>ℹ️</span>
          <div style={{ color: '#166534', flex: 1 }}>{importReviewNotice}</div>
        </div>
      ) : null}

      {farmId && productionUnitId && (
        <>
          {isSmallScreen ? (
            <div
              role="tablist"
              aria-label={t('feed.aria.panels')}
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 16,
                padding: 6,
                borderRadius: 10,
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff'
              }}
            >
              <button
                role="tab"
                aria-selected={activePanel === 'log'}
                type="button"
                onClick={() => setActivePanel('log')}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  backgroundColor: activePanel === 'log' ? '#3b82f6' : '#f3f4f6',
                  color: activePanel === 'log' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {t('feed.panel.log')}
              </button>
              <button
                role="tab"
                aria-selected={activePanel === 'analyze'}
                type="button"
                onClick={() => setActivePanel('analyze')}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  backgroundColor: activePanel === 'analyze' ? '#3b82f6' : '#f3f4f6',
                  color: activePanel === 'analyze' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {t('feed.panel.analyze')}
              </button>
            </div>
          ) : null}

          <div
            style={
              isSmallScreen
                ? undefined
                : {
                    display: showLogPanel ? 'grid' : 'block',
                    gridTemplateColumns: showLogPanel ? 'minmax(340px, 520px) 1fr' : undefined,
                    gap: showLogPanel ? 24 : undefined,
                    alignItems: 'start'
                  }
            }
          >
            {showLogPanel ? (
              <div>
                <div className="form-card" style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 12,
                      flexWrap: 'wrap'
                    }}
                  >
                    <div style={{ minWidth: 220, flex: '1 1 320px' }}>
                      <h3 style={{ marginTop: 0, marginBottom: 6 }}>{t('feed.section.actualFed.title')}</h3>
                      <p className="lead" style={{ marginTop: 0, marginBottom: 0 }}>
                        {t('feed.section.actualFed.lead')}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-gradient"
                      disabled={!farmId || !productionUnitId || feedEventsLoading}
                      onClick={() => {
                        void handleManualRefresh()
                      }}
                      title={t('feed.button.refreshEventsTooltip')}
                    >
                      {t('feed.button.refreshEvents')}
                    </button>
                  </div>
                  <p style={{ margin: '10px 0 0 0', fontSize: 13, color: '#0f766e' }}>
                    {t('feed.instructions.refreshHint')}
                  </p>
                  {!canEditFeedEvents && (
                    <p className="lead" style={{ marginTop: 0, fontSize: 12, opacity: 0.8 }}>
                      {t('feed.readonly.banner', { role: principalRoleLabel })}
                    </p>
                  )}

                  {feedEventStatusMessage && computeSyncBannerVisibility({
                    hasActionMessage: Boolean(feedEventStatusMessage),
                    hasAttentionMessage: Boolean(feedSyncAttentionMessage),
                    hasLastResultSummary: Boolean(feedSyncLastResultSummary)
                  }).showActionMessage && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 8,
                        backgroundColor: '#ecfdf5',
                        color: '#047857'
                      }}
                    >
                      {feedEventStatusMessage}
                    </div>
                  )}

                  {feedSyncLastResultSummary && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 8,
                        backgroundColor: '#eff6ff',
                        color: '#1d4ed8'
                      }}
                    >
                      <div>{feedSyncLastResultSummary.resultLabel}</div>
                      <div>{feedSyncLastResultSummary.attemptedAtLabel}</div>
                    </div>
                  )}

                  {feedSyncAttentionMessage && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 8,
                        backgroundColor: '#fff7ed',
                        color: '#9a3412'
                      }}
                    >
                      <div>{feedSyncAttentionMessage}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn-gradient"
                          onClick={() => {
                            void handleFeedSyncRetry()
                          }}
                          disabled={retryingFeedSync || feedEventsLoading}
                        >
                          {retryingFeedSync ? t('feed.sync.retrying') : t('feed.sync.retryAction')}
                        </button>
                        <button
                          type="button"
                          className="btn-gradient"
                          onClick={() => {
                            void handleManualRefresh()
                          }}
                          disabled={feedEventsLoading || retryingFeedSync}
                        >
                          {feedEventsLoading ? t('feed.sync.refreshing') : t('feed.sync.refreshAction')}
                        </button>
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 12,
                      alignItems: 'end'
                    }}
                  >
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600 }}>
                      {t('feed.form.recordedAt')}
                      <DateTimeLocalInput
                        value={feedEventDraft.recordedAt}
                        timeZone={activeTimeZone}
                        disabled={!canEditFeedEvents}
                        onChangeValue={(value) => setFeedEventDraft((prev) => ({ ...prev, recordedAt: value }))}
                      />
                    </label>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600 }}>
                      {t('feed.form.lot')}
                      <select
                        value={feedEventDraft.lot}
                        onChange={(e) => setFeedEventDraft((prev) => ({ ...prev, lot: e.target.value }))}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5', backgroundColor: '#ffffff' }}
                        disabled={!canEditFeedEvents}
                      >
                        {availableLots.length === 0 ? (
                          <option value="">{lotsLoading ? t('feed.lots.loadingLots') : t('feed.lots.noneLoaded')}</option>
                        ) : (
                          <>
                            {availableLots.map((lot) => (
                              <option key={lot.id} value={lot.lotKey} disabled={!isLotActiveForInteraction(lot.stage)}>
                                {lot.lotKey}
                                {lot.stage
                                  ? ` (${lot.stage === 'Active'
                                    ? t('datatable.stage.active')
                                    : lot.stage === 'Inactive'
                                      ? t('datatable.stage.inactive')
                                      : lot.stage
                                  })`
                                  : ''}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </label>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600 }}>
                      {t('feed.form.feedKg')}
                      <input
                        value={feedEventDraft.feedKg}
                        onChange={(e) => setFeedEventDraft((prev) => ({ ...prev, feedKg: e.target.value }))}
                        placeholder={t('feed.form.feedKg.placeholder')}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                        disabled={!canEditFeedEvents}
                      />
                    </label>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600 }}>
                      {t('feed.form.feedType')}
                      <input
                        value={feedEventDraft.feedType}
                        onChange={(e) => setFeedEventDraft((prev) => ({ ...prev, feedType: e.target.value }))}
                        placeholder={t('feed.form.feedType.placeholder')}
                        list={feedTypeSuggestions.length > 0 ? 'feed-type-suggestions' : undefined}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                        disabled={!canEditFeedEvents}
                      />
                      {feedTypeSuggestions.length > 0 ? (
                        <>
                          <datalist id="feed-type-suggestions">
                            {feedTypeSuggestions.map((value) => (
                              <option key={value} value={value} />
                            ))}
                          </datalist>
                          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                            {t('feed.form.feedType.suggestionsHelp', { days: FEED_TYPE_SUGGESTION_WINDOW_DAYS })}
                          </div>
                        </>
                      ) : null}
                    </label>
                  </div>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, marginTop: 12, maxWidth: 320 }}>
                    {t('feed.form.feedingResponse')}
                    <select
                      data-testid="feed-form-feeding-response"
                      value={feedEventDraft.feedingResponse}
                      onChange={(e) => setFeedEventDraft((prev) => ({ ...prev, feedingResponse: e.target.value as FeedingResponse | '' }))}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
                      disabled={!canEditFeedEvents}
                    >
                      <option value="">{t('feed.feedingResponse.notRecorded')}</option>
                      {FEEDING_RESPONSES.map((response) => (
                        <option key={response} value={response}>{t(`feed.feedingResponse.${response}`)}</option>
                      ))}
                    </select>
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{t('feed.form.feedingResponseHelp')}</span>
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, marginTop: 12 }}>
                    {t('feed.form.notes')}
                    <textarea
                      value={feedEventDraft.notes}
                      onChange={(e) => setFeedEventDraft((prev) => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5', resize: 'vertical' }}
                      disabled={!canEditFeedEvents}
                    />
                  </label>

                  {(lotsError || feedEventsError) && (
                    <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 14 }}>
                      {lotsError ? `${t('feed.errors.lotsPrefix')} ${lotsError}` : null}
                      {lotsError && feedEventsError ? ' · ' : null}
                      {feedEventsError ? `${t('feed.errors.eventsPrefix')} ${feedEventsError}` : null}
                    </div>
                  )}

                  {feedEventSaveError && (
                    <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 14 }}>
                      {feedEventSaveError}
                    </div>
                  )}

                  {selectedUnitInactive ? (
                    <div data-testid="feed-unit-inactive-notice" role="status" style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 10, border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#92400e', fontSize: 13 }}>
            {t('unit.inactive.captureBlocked')}
          </div>
                  ) : null}

                  <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-gradient"
                      disabled={!canEditFeedEvents || feedEventSaving || interactiveLots.length === 0 || selectedUnitInactive}
                      onClick={async () => {
                        if (!canEditFeedEvents) {
                          setFeedEventSaveError(t('feed.error.readonlyCannotCreateOrEdit'))
                          return
                        }
                        if (!farmId || !productionUnitId) return

                        const selectedLot = availableLots.find((lot) => lot.lotKey === feedEventDraft.lot)
                        if (!selectedLot || !isLotActiveForInteraction(selectedLot.stage)) {
                          setFeedEventSaveError(t('feed.error.activeLotRequired'))
                          return
                        }

                        const trimmedFeed = feedEventDraft.feedKg.trim()
                        if (!trimmedFeed) {
                          setFeedEventSaveError(t('feed.error.feedKgRequired'))
                          return
                        }

                        const candidateNumber = Number(trimmedFeed.replace(',', '.'))
                        if (!Number.isFinite(candidateNumber) || candidateNumber < 0) {
                          setFeedEventSaveError(t('feed.error.feedKgNonNegative'))
                          return
                        }

                        setFeedEventSaving(true)
                        setFeedEventSaveError(null)
                        clearFeedEventStatus()

                        try {
                          // Findings #66: this form only creates. Existing events
                          // are changed through "Corregir…" (audited, reason
                          // required).
                          const result = await feedLogApi.create({
                            farmId,
                            productionUnitId,
                            recordedAt: feedEventDraft.recordedAt,
                            // v2.2 T3-B-06a: send canonical `lotId` so the FeedLog
                            // is queryable by lot without a lotKey lookup and the
                            // cost-per-kg dataset picks it up in its primary path.
                            lotId: selectedLot.id,
                            lot: feedEventDraft.lot,
                            feedKg: trimmedFeed,
                            feedType: feedEventDraft.feedType,
                            notes: feedEventDraft.notes,
                            ...(feedEventDraft.feedingResponse
                              ? { metadata: { feedingResponse: feedEventDraft.feedingResponse } }
                              : {})
                          })
                          applyFeedEventStatus('saved', result)

                          const logs = await feedLogApi.list({ farmId, productionUnitId }, { preferLocal: true })
                          setFeedEventsAll(logs)
                          setFeedEvents(logs.slice(0, 50))

                          setFeedEventDraft((prev) => ({
                            ...prev,
                            editingId: null,
                            feedKg: '',
                            feedType: '',
                            notes: '',
                            feedingResponse: ''
                          }))
                        } catch (error) {
                          clearFeedEventStatus()
                          const message = error instanceof Error ? error.message : t('feed.error.unableToSaveEvent')
                          setFeedEventSaveError(message)
                        } finally {
                          setFeedEventSaving(false)
                        }
                      }}
                    >
                      {t('feed.button.addEvent')}
                    </button>
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('feed.section.recentEvents')}</div>
                    {feedEventsLoading ? (
                      <div className="lead">{t('feed.status.loading')}</div>
                    ) : feedEvents.length === 0 ? (
                      <div className="lead">{t('feed.empty.noFeedEvents')}</div>
                    ) : (
                      <div
                        style={{
                          maxHeight: isSmallScreen
                            ? 'clamp(180px, 30vh, 320px)'
                            : 'clamp(380px, 55vh, 480px)',
                          overflowY: 'auto',
                          overflowX: 'auto',
                          WebkitOverflowScrolling: 'touch'
                        }}
                      >
                        <DeletedLotRowsLine
                          count={hiddenDeletedLotFeedCount}
                          shown={showDeletedLotFeed}
                          onToggle={() => setShowDeletedLotFeed((value) => !value)}
                          testId="feed-hidden-deleted-lot-rows"
                        />
                        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                              <th style={{ padding: 10, border: '1px solid #e5e7eb' }}>{t('feed.table.recorded')}</th>
                              <th style={{ padding: 10, border: '1px solid #e5e7eb' }}>{t('feed.table.lot')}</th>
                              <th style={{ padding: 10, border: '1px solid #e5e7eb' }}>{t('feed.table.feedKg')}</th>
                              <th style={{ padding: 10, border: '1px solid #e5e7eb' }}>{t('feed.table.type')}</th>
                              <th style={{ padding: 10, border: '1px solid #e5e7eb' }}>{t('feed.table.notes')}</th>
                              <th style={{ padding: 10, border: '1px solid #e5e7eb' }}>{t('feed.table.actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleFeedEvents.map((log) => {
                              const cost = costsByFeedLogId.get(log.id)
                              const isDeletedLotLog = isDeletedLotFeed(log)
                              return (
                              <tr
                                key={log.id}
                                style={isDeletedLotLog ? DELETED_LOT_ROW_STYLE : undefined}
                                title={isDeletedLotLog ? t('datatable.deletedLot.readOnly') : undefined}
                                data-deleted-lot={isDeletedLotLog ? 'true' : undefined}
                              >
                                <td style={{ padding: 10, border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                                  {formatStoredDateTimeAsLocal(log.recordedAt)}
                                  <CrewRowBadges
                                    captureSource={log.captureSource}
                                    capturedByActorId={log.capturedByActorId}
                                    correctionCount={feedCorrectionCounts.get(log.id) ?? 0}
                                    actorNames={actorNames}
                                    testId={`feed-row-badges-${log.id}`}
                                  />
                                </td>
                                <td style={{ padding: 10, border: '1px solid #e5e7eb' }}>{(log.lot ?? '').trim() || lotLabelById.get(String(log.lotId ?? '')) || ''}</td>
                                <td style={{ padding: 10, border: '1px solid #e5e7eb' }}>
                                  {log.feedKg ?? ''}
                                  {cost ? (
                                    <div
                                      data-testid={`feed-cost-badge-${log.id}`}
                                      style={{ fontSize: 11, color: '#059669', fontWeight: 600, marginTop: 2 }}
                                      title={t('feed.table.costBadgeTooltip')}
                                    >
                                      ≈ {formatCostEventDisplayAmount(cost.amountCents, cost.currency)}
                                    </div>
                                  ) : null}
                                </td>
                                <td style={{ padding: 10, border: '1px solid #e5e7eb' }}>
                                  {log.feedType ?? ''}
                                  {readFeedLogMetaString(log, 'mixName') || readFeedLogMetaString(log, 'feedLotNumber') || readDayWeighing(log) ? (
                                    <div data-testid={`feed-row-trace-${log.id}`} style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                      {[
                                        readFeedLogMetaString(log, 'mixName')
                                          ? t('feed.table.mixPart', { mix: readFeedLogMetaString(log, 'mixName') ?? '' })
                                          : null,
                                        readFeedLogMetaString(log, 'feedLotNumber')
                                          ? t('feed.table.feedLot', { lot: readFeedLogMetaString(log, 'feedLotNumber') ?? '' })
                                          : null,
                                        // Findings #182: "Pesada del día" — what was weighed and left over.
                                        (() => {
                                          const weighing = readDayWeighing(log)
                                          if (!weighing) return null
                                          return weighing.leftoverKg === null
                                            ? t('feed.table.weighedNoLeftover', { weighed: String(weighing.weighedKg) })
                                            : t('feed.table.weighed', { weighed: String(weighing.weighedKg), leftover: String(weighing.leftoverKg) })
                                        })()
                                      ].filter(Boolean).join(' · ')}
                                    </div>
                                  ) : null}
                                </td>
                                <td style={{ padding: 10, border: '1px solid #e5e7eb' }}>
                                  {(() => {
                                    const response = readFeedLogMetaString(log, 'feedingResponse')
                                    return response && (FEEDING_RESPONSES as string[]).includes(response) ? (
                                      <div data-testid={`feed-row-response-${log.id}`} style={{ fontSize: 12, fontWeight: 700, color: '#0f766e' }}>
                                        {t('feed.table.response', { response: t(`feed.feedingResponse.${response as FeedingResponse}`) })}
                                      </div>
                                    ) : null
                                  })()}
                                  {log.notes ?? ''}
                                </td>
                                <td style={{ padding: 10, border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <CrewCorrectionButton
                                      resource="feed-log"
                                      entityId={log.id}
                                      farmId={farmId}
                                      disabled={!canEditFeedEvents || isDeletedLotLog}
                                      onDone={async () => {
                                        if (!farmId || !productionUnitId) return
                                        // Findings #66: edits, moves and deletes all go
                                        // through the audited correction flow and land on
                                        // the server (a delete has synced by now), so
                                        // reload from there; offline, keep the local copy.
                                        const logs = await feedLogApi.list(
                                          { farmId, productionUnitId },
                                          { forceRemote: true }
                                        ).catch(() => feedLogApi.list({ farmId, productionUnitId }, { preferLocal: true }))
                                        setFeedEventsAll(logs)
                                        setFeedEvents(logs.slice(0, 50))
                                        setFeedCorrectionsRefreshKey((key) => key + 1)
                                      }}
                                    />
                                  </div>
                                </td>
                              </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="lead" style={{ marginTop: 10 }}>
                      {t('feed.note.recommendedVsActual')}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {showAnalyzePanel ? (
              <div>
                {/* View Mode Toggle */}
                <div style={{ marginBottom: '20px' }}>
                  <div className="responsive-actions">
                    <button
                      onClick={() => setViewMode('daily')}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: viewMode === 'daily' ? '#3b82f6' : '#f3f4f6',
                        color: viewMode === 'daily' ? 'white' : '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {t('feed.viewMode.daily')}
                    </button>
                    <button
                      onClick={() => setViewMode('weekly')}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: viewMode === 'weekly' ? '#3b82f6' : '#f3f4f6',
                        color: viewMode === 'weekly' ? 'white' : '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {t('feed.viewMode.weekly')}
                    </button>
                  </div>
                </div>

                {/* Panel Toggle (like Biomass: KPIs / Breakdown / Charts) */}
                <div className="responsive-actions" style={{ marginBottom: '20px' }}>
                  <button
                    onClick={() => setPanelMode('kpis')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: panelMode === 'kpis' ? '#3b82f6' : '#f3f4f6',
                      color: panelMode === 'kpis' ? 'white' : '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('feed.panel.kpis')}
                  </button>
                  <button
                    onClick={() => setPanelMode('breakdown')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: panelMode === 'breakdown' ? '#3b82f6' : '#f3f4f6',
                      color: panelMode === 'breakdown' ? 'white' : '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('feed.panel.breakdown')}
                  </button>
                  <button
                    onClick={() => setPanelMode('charts')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: panelMode === 'charts' ? '#3b82f6' : '#f3f4f6',
                      color: panelMode === 'charts' ? 'white' : '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('feed.panel.charts')}
                  </button>
                </div>

                {!isSmallScreen && panelMode !== 'kpis' ? (
                  <div style={{ marginBottom: 20 }}>
                    <button
                      type="button"
                      onClick={() => setDesktopLogVisible((prev) => !prev)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: desktopLogVisible ? '#f3f4f6' : '#3b82f6',
                        color: desktopLogVisible ? '#374151' : 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {desktopLogVisible ? t('feed.button.hideLog') : t('feed.button.showLog')}
                    </button>
                  </div>
                ) : null}

          {feedKPIs.length === 0 ? (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px',
              border: '2px solid #f59e0b'
            }}>
              <h3 style={{ color: '#92400e', marginTop: 0 }}>{t('feed.empty.noData.title')}</h3>
              <p style={{ color: '#92400e', margin: 0 }}>
                {t('feed.empty.noData.intro')}
                <br />• {t('feed.empty.noData.item.database', { stockMetric: stockMetricLabel })}
                <br />• {t('feed.empty.noData.item.water')}
                <br />• {t('feed.empty.noData.item.lots')}
              </p>
            </div>
          ) : (
            <>
              {panelMode === 'kpis' ? (
                <>
                  {/* KPI Cards (newest periods first) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
                    {showSplitKpiCards
                      ? desktopTopKpiCards.map(renderKpiCard)
                      : visibleSortedFeedKPIs.map(renderKpiCard)}
                  </div>
                  {!showSplitKpiCards ? (
                    <>
                      {renderLoadOlderWeeks()}
                      {renderSummaryStats()}
                    </>
                  ) : null}
                </>
              ) : panelMode === 'breakdown' ? (
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{ marginTop: 0, color: '#475569' }}>
                    {t('feed.breakdown.title', { period: viewMode === 'daily' ? t('feed.period.day') : t('feed.period.week') })}
                  </h4>
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.period')}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.lot')}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'right',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.biomassKg')}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'right',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.sfrPct')}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'right',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.feedKg', {
                              period: viewMode === 'daily' ? t('feed.period.daily') : t('feed.period.weekly')
                            })}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'right',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.actualKg')}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'right',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.adherence')}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.events')}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'right',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.fcr')}
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            {t('feed.breakdown.column.status')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedFeedKPIs.map((kpi, index) => (
                          <tr key={`${kpi.lot}-${kpi.week}`} style={{
                            backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                            borderBottom: index === sortedFeedKPIs.length - 1 ? 'none' : '1px solid #f3f4f6'
                          }}>
                            <td style={{
                              padding: '12px 16px',
                              color: '#6b7280',
                              fontSize: '14px'
                            }}>
                              {kpi.weekDisplay}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              color: '#374151',
                              fontWeight: '500'
                            }}>
                              {kpi.lot}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              color: '#059669',
                              fontWeight: '600'
                            }}>
                              {kpi.biomassKg.toFixed(biomassDecimals)}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              color: '#0284c7',
                              fontWeight: '600'
                            }}>
                              {kpi.sfrPercent.toFixed(2)}%
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              color: '#dc2626',
                              fontWeight: '600'
                            }}>
                              {(viewMode === 'daily' ? kpi.dailyFeedKg : kpi.weeklyFeedKg).toFixed(2)}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              color: '#7c3aed',
                              fontWeight: '600'
                            }}>
                              {(viewMode === 'daily' ? kpi.actualDailyFeedKg : kpi.actualWeeklyFeedKg).toFixed(2)}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              color: '#374151',
                              fontWeight: '600'
                            }}>
                              {(() => {
                                const recommended = viewMode === 'daily' ? kpi.dailyFeedKg : kpi.weeklyFeedKg
                                const actual = viewMode === 'daily' ? kpi.actualDailyFeedKg : kpi.actualWeeklyFeedKg
                                if (recommended <= 0) return '—'
                                return `${Math.round((actual / recommended) * 100)}%`
                              })()}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'center',
                              color: '#6b7280',
                              fontWeight: '600'
                            }}>
                              {kpi.actualEventCount}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              color: kpi.fcr ? '#ea580c' : '#9ca3af',
                              fontWeight: '600'
                            }}>
                              {kpi.fcr ? kpi.fcr.toFixed(fcrDecimals) : t('feed.value.na')}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'center'
                            }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                backgroundColor: kpi.dataQuality.completeness >= 100 ? '#dcfce7' : '#fee2e2',
                                color: kpi.dataQuality.completeness >= 100 ? '#166534' : '#dc2626'
                              }}>
                                {kpi.dataQuality.completeness >= 100 ? t('feed.quality.complete') : t('feed.quality.incomplete')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ marginBottom: '10px', color: '#374151' }}>{t('feed.charts.heading')}</h3>
                    <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                      {t('feed.charts.lead')}
                    </p>
                    {isSmallScreen && (
                      <p style={{ color: '#6b7280', fontSize: '13px', margin: '8px 0 0' }}>
                        {t('feed.charts.tipLandscape')}
                      </p>
                    )}
                  </div>

                  <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    {feedStatsByWeek.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        {t('feed.charts.noData')}
                      </div>
                    ) : (() => {
                      const chartWidth = isSmallScreen ? 760 : 1000
                      const barHeight = isSmallScreen ? 22 : 28
                      const barSpacing = 10
                      const padding = isSmallScreen
                        ? { top: 40, right: 110, bottom: 40, left: 120 }
                        : { top: 40, right: 140, bottom: 40, left: 260 }
                      const chartHeight = feedStatsByWeek.length * (barHeight + barSpacing) + padding.top + padding.bottom

                      const maxFeed = Math.max(
                        ...feedStatsByWeek.map((d) => Math.max(d.totalFeedKg, d.totalActualFeedKg))
                      )
                      const barMaxWidth = chartWidth - padding.left - padding.right
                      const tickRatios = isSmallScreen ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]

                      return (
                        <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <svg width={chartWidth} height={chartHeight} style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                            <text x={chartWidth / 2} y={25} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#374151">
                              {t('feed.charts.recommendedVsActualTitle', {
                                period: viewMode === 'daily' ? t('feed.period.daily') : t('feed.period.weekly')
                              })}
                            </text>

                            {feedStatsByWeek.map((d, i) => (
                              <text
                                key={d.week}
                                x={padding.left - 10}
                                y={padding.top + i * (barHeight + barSpacing) + barHeight / 2 + 4}
                                textAnchor="end"
                                fontSize={isSmallScreen ? '11' : '12'}
                                fill="#374151"
                              >
                                <title>{d.weekDisplay}</title>
                                {isSmallScreen ? d.week : d.weekDisplay}
                              </text>
                            ))}

                            {feedStatsByWeek.map((d, i) => {
                              const recommendedWidth = maxFeed > 0 ? (d.totalFeedKg / maxFeed) * barMaxWidth : 0
                              const actualWidth = maxFeed > 0 ? (d.totalActualFeedKg / maxFeed) * barMaxWidth : 0
                              const y = padding.top + i * (barHeight + barSpacing)
                              const innerGap = 2
                              const segmentHeight = Math.max(6, (barHeight - innerGap) / 2)
                              const labelX = padding.left + Math.max(recommendedWidth, actualWidth) + 8
                              return (
                                <g key={d.week}>
                                  <rect x={padding.left} y={y} width={barMaxWidth} height={barHeight} fill="#f3f4f6" rx={4} />
                                  <rect x={padding.left} y={y} width={recommendedWidth} height={segmentHeight} fill="#dc2626" rx={4}>
                                    <title>{`${d.weekDisplay}\nRecommended: ${d.totalFeedKg.toFixed(2)} kg\nActual: ${d.totalActualFeedKg.toFixed(2)} kg (${d.totalActualEventCount} events)\nBiomass: ${d.totalBiomassKg.toFixed(1)} kg\nLots: ${d.completeLotsCount}/${d.totalLotsCount} complete`}</title>
                                  </rect>
                                  <rect x={padding.left} y={y + segmentHeight + innerGap} width={actualWidth} height={segmentHeight} fill="#7c3aed" rx={4}>
                                    <title>{`${d.weekDisplay}\nRecommended: ${d.totalFeedKg.toFixed(2)} kg\nActual: ${d.totalActualFeedKg.toFixed(2)} kg (${d.totalActualEventCount} events)\nBiomass: ${d.totalBiomassKg.toFixed(1)} kg\nLots: ${d.completeLotsCount}/${d.totalLotsCount} complete`}</title>
                                  </rect>
                                  <text x={labelX} y={y + barHeight / 2 + 4} fontSize="12" fill="#374151" fontWeight="500">
                                    R: {d.totalFeedKg.toFixed(1)} kg / A: {d.totalActualFeedKg.toFixed(1)} kg
                                  </text>
                                </g>
                              )
                            })}

                            {tickRatios.map((ratio) => {
                              const x = padding.left + ratio * barMaxWidth
                              const value = Math.round(ratio * maxFeed)
                              return (
                                <g key={ratio}>
                                  <line x1={x} y1={padding.top} x2={x} y2={chartHeight - padding.bottom} stroke="#e5e7eb" strokeWidth={1} strokeDasharray="3,3" />
                                  <text x={x} y={chartHeight - padding.bottom + 15} textAnchor="middle" fontSize="11" fill="#6b7280">
                                    {value.toLocaleString()}
                                  </text>
                                </g>
                              )
                            })}

                            <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="12" fill="#6b7280">
                              {t('feed.charts.axis.feedKg')}
                            </text>
                          </svg>
                        </div>
                      )
                    })()}
                  </div>

                  <div style={{
                    marginTop: '20px',
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    {feedStatsByWeek.length === 0 ? null : (() => {
                      const chartWidth = isSmallScreen ? 760 : 1000
                      const barHeight = isSmallScreen ? 20 : 22
                      const barSpacing = 10
                      const padding = isSmallScreen
                        ? { top: 40, right: 100, bottom: 40, left: 120 }
                        : { top: 40, right: 120, bottom: 40, left: 260 }
                      const chartHeight = feedStatsByWeek.length * (barHeight + barSpacing) + padding.top + padding.bottom

                      const maxSfr = Math.max(1, ...feedStatsByWeek.map((d) => d.sfrWeightedAvgPercent || 0))
                      const barMaxWidth = chartWidth - padding.left - padding.right
                      const tickRatios = isSmallScreen ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]

                      return (
                        <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <svg width={chartWidth} height={chartHeight} style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                            <text x={chartWidth / 2} y={25} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#374151">
                              {t('feed.charts.sfrTitle')}
                            </text>

                            {feedStatsByWeek.map((d, i) => (
                              <text
                                key={d.week}
                                x={padding.left - 10}
                                y={padding.top + i * (barHeight + barSpacing) + barHeight / 2 + 4}
                                textAnchor="end"
                                fontSize={isSmallScreen ? '11' : '12'}
                                fill="#374151"
                              >
                                <title>{d.weekDisplay}</title>
                                {isSmallScreen ? d.week : d.weekDisplay}
                              </text>
                            ))}

                            {feedStatsByWeek.map((d, i) => {
                              const value = d.completeLotsCount > 0 ? d.sfrWeightedAvgPercent : 0
                              const barWidth = maxSfr > 0 ? (value / maxSfr) * barMaxWidth : 0
                              const y = padding.top + i * (barHeight + barSpacing)
                              return (
                                <g key={d.week}>
                                  <rect x={padding.left} y={y} width={barMaxWidth} height={barHeight} fill="#f3f4f6" rx={4} />
                                  <rect x={padding.left} y={y} width={barWidth} height={barHeight} fill="#0284c7" rx={4}>
                                    <title>{`${d.weekDisplay}\nAvg SFR (weighted): ${value.toFixed(2)}%\nAvg SFR (mean): ${d.sfrAvgPercent.toFixed(2)}%\nLots: ${d.completeLotsCount}/${d.totalLotsCount} complete`}</title>
                                  </rect>
                                  <text x={padding.left + barWidth + 8} y={y + barHeight / 2 + 4} fontSize="12" fill="#374151" fontWeight="500">
                                    {d.completeLotsCount > 0 ? `${value.toFixed(2)}%` : t('feed.value.na')}
                                  </text>
                                </g>
                              )
                            })}

                            {tickRatios.map((ratio) => {
                              const x = padding.left + ratio * barMaxWidth
                              const value = ratio * maxSfr
                              return (
                                <g key={ratio}>
                                  <line x1={x} y1={padding.top} x2={x} y2={chartHeight - padding.bottom} stroke="#e5e7eb" strokeWidth={1} strokeDasharray="3,3" />
                                  <text x={x} y={chartHeight - padding.bottom + 15} textAnchor="middle" fontSize="11" fill="#6b7280">
                                    {value.toFixed(1)}
                                  </text>
                                </g>
                              )
                            })}

                            <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="12" fill="#6b7280">
                              {t('feed.charts.axis.sfrPct')}
                            </text>
                          </svg>
                        </div>
                      )
                    })()}
                  </div>

                  <div style={{
                    marginTop: '20px',
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    {(() => {
                      const rows = feedStatsByWeek.filter((d) => typeof d.fcrAvg === 'number' && Number.isFinite(d.fcrAvg))
                      if (rows.length === 0) {
                        return (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                            {t('feed.charts.noFcr')}
                          </div>
                        )
                      }

                      const chartWidth = isSmallScreen ? 760 : 1000
                      const barHeight = isSmallScreen ? 20 : 22
                      const barSpacing = 10
                      const padding = isSmallScreen
                        ? { top: 40, right: 100, bottom: 40, left: 120 }
                        : { top: 40, right: 120, bottom: 40, left: 260 }
                      const chartHeight = rows.length * (barHeight + barSpacing) + padding.top + padding.bottom

                      const maxFcr = Math.max(1, ...rows.map((d) => d.fcrAvg ?? 0))
                      const barMaxWidth = chartWidth - padding.left - padding.right
                      const tickRatios = isSmallScreen ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]

                      return (
                        <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <svg width={chartWidth} height={chartHeight} style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                            <text x={chartWidth / 2} y={25} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#374151">
                              {t('feed.charts.fcrTitle')}
                            </text>

                            {rows.map((d, i) => (
                              <text
                                key={d.week}
                                x={padding.left - 10}
                                y={padding.top + i * (barHeight + barSpacing) + barHeight / 2 + 4}
                                textAnchor="end"
                                fontSize={isSmallScreen ? '11' : '12'}
                                fill="#374151"
                              >
                                <title>{d.weekDisplay}</title>
                                {isSmallScreen ? d.week : d.weekDisplay}
                              </text>
                            ))}

                            {rows.map((d, i) => {
                              const value = d.fcrAvg ?? 0
                              const barWidth = maxFcr > 0 ? (value / maxFcr) * barMaxWidth : 0
                              const y = padding.top + i * (barHeight + barSpacing)
                              return (
                                <g key={d.week}>
                                  <rect x={padding.left} y={y} width={barMaxWidth} height={barHeight} fill="#f3f4f6" rx={4} />
                                  <rect x={padding.left} y={y} width={barWidth} height={barHeight} fill="#ea580c" rx={4}>
                                    <title>{`${d.weekDisplay}\nAvg FCR: ${value.toFixed(fcrDecimals)}\nSamples: ${d.fcrCount} lots (complete)`}</title>
                                  </rect>
                                  <text x={padding.left + barWidth + 8} y={y + barHeight / 2 + 4} fontSize="12" fill="#374151" fontWeight="500">
                                    {value.toFixed(fcrDecimals)}
                                  </text>
                                </g>
                              )
                            })}

                            {tickRatios.map((ratio) => {
                              const x = padding.left + ratio * barMaxWidth
                              const value = ratio * maxFcr
                              return (
                                <g key={ratio}>
                                  <line x1={x} y1={padding.top} x2={x} y2={chartHeight - padding.bottom} stroke="#e5e7eb" strokeWidth={1} strokeDasharray="3,3" />
                                  <text x={x} y={chartHeight - padding.bottom + 15} textAnchor="middle" fontSize="11" fill="#6b7280">
                                    {value.toFixed(2)}
                                  </text>
                                </g>
                              )
                            })}

                            <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="12" fill="#6b7280">
                              {t('feed.charts.axis.fcr')}
                            </text>
                          </svg>
                        </div>
                      )
                    })()}
                  </div>

                  <div style={{
                    marginTop: '20px',
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    {(() => {
                      const points = feedStatsByWeek
                        .filter((d) => typeof d.fcrAvg === 'number' && typeof d.sgrAvg === 'number' && d.completeLotsCount > 0)
                        .map((d) => ({
                          week: d.week,
                          weekDisplay: d.weekDisplay,
                          sfr: d.sfrWeightedAvgPercent,
                          sgr: d.sgrAvg as number,
                          fcr: d.fcrAvg as number
                        }))

                      if (points.length === 0) {
                        return (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                            {t('feed.charts.relationshipNeedsData')}
                          </div>
                        )
                      }

                      const chartWidth = isSmallScreen ? 820 : 1000
                      const chartHeight = isSmallScreen ? 360 : 420
                      const padding = isSmallScreen
                        ? { top: 44, right: 56, bottom: 62, left: 56 }
                        : { top: 48, right: 80, bottom: 70, left: 80 }
                      const plotWidth = chartWidth - padding.left - padding.right
                      const plotHeight = chartHeight - padding.top - padding.bottom

                      const maxSfr = Math.max(1, ...points.map((p) => p.sfr))
                      const maxSgr = Math.max(0.1, ...points.map((p) => p.sgr))
                      const maxFcr = Math.max(0.1, ...points.map((p) => p.fcr))

                      const xFor = (sfr: number) => padding.left + (sfr / maxSfr) * plotWidth
                      const yLeftFor = (sgr: number) => padding.top + (1 - (sgr / maxSgr)) * plotHeight
                      const yRightFor = (fcr: number) => padding.top + (1 - (fcr / maxFcr)) * plotHeight

                      const tickCount = isSmallScreen ? 3 : 5
                      const ticks = Array.from({ length: tickCount + 1 }, (_, i) => i / tickCount)

                      return (
                        <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <svg width={chartWidth} height={chartHeight} style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                            <text x={chartWidth / 2} y={26} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#374151">
                              {t('feed.charts.relationshipTitle')}
                            </text>

                            {/* X axis grid + labels */}
                            {ticks.map((ratio) => {
                              const x = padding.left + ratio * plotWidth
                              const value = ratio * maxSfr
                              return (
                                <g key={`x-${ratio}`}>
                                  <line x1={x} y1={padding.top} x2={x} y2={padding.top + plotHeight} stroke="#e5e7eb" strokeDasharray="3,3" />
                                  <text x={x} y={padding.top + plotHeight + 22} textAnchor="middle" fontSize="11" fill="#6b7280">
                                    {value.toFixed(1)}
                                  </text>
                                </g>
                              )
                            })}

                            {/* Left Y axis grid + labels (SGR) */}
                            {ticks.map((ratio) => {
                              const y = padding.top + (1 - ratio) * plotHeight
                              const value = ratio * maxSgr
                              return (
                                <g key={`y-left-${ratio}`}>
                                  <line x1={padding.left} y1={y} x2={padding.left + plotWidth} y2={y} stroke="#f1f5f9" />
                                  <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                                    {value.toFixed(2)}
                                  </text>
                                </g>
                              )
                            })}

                            {/* Right Y axis labels (FCR) */}
                            {ticks.map((ratio) => {
                              const y = padding.top + (1 - ratio) * plotHeight
                              const value = ratio * maxFcr
                              return (
                                <text key={`y-right-${ratio}`} x={padding.left + plotWidth + 10} y={y + 4} textAnchor="start" fontSize="11" fill="#6b7280">
                                  {value.toFixed(2)}
                                </text>
                              )
                            })}

                            {/* Axes */}
                            <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + plotHeight} stroke="#94a3b8" />
                            <line x1={padding.left + plotWidth} y1={padding.top} x2={padding.left + plotWidth} y2={padding.top + plotHeight} stroke="#94a3b8" />
                            <line x1={padding.left} y1={padding.top + plotHeight} x2={padding.left + plotWidth} y2={padding.top + plotHeight} stroke="#94a3b8" />

                            {/* Axis labels */}
                            <text x={padding.left - 48} y={padding.top + plotHeight / 2} textAnchor="middle" fontSize="12" fill="#7c3aed" transform={`rotate(-90 ${padding.left - 48} ${padding.top + plotHeight / 2})`}>
                              {t('feed.charts.axis.sgr')}
                            </text>
                            <text x={padding.left + plotWidth + 48} y={padding.top + plotHeight / 2} textAnchor="middle" fontSize="12" fill="#ea580c" transform={`rotate(90 ${padding.left + plotWidth + 48} ${padding.top + plotHeight / 2})`}>
                              {t('feed.charts.axis.fcr')}
                            </text>
                            <text x={padding.left + plotWidth / 2} y={padding.top + plotHeight + 52} textAnchor="middle" fontSize="12" fill="#0284c7">
                              {t('feed.charts.axis.sfrPct')}
                            </text>

                            {/* Legend */}
                            <g transform={`translate(${padding.left + 10}, ${padding.top - 22})`}>
                              <circle cx={0} cy={0} r={5} fill="#7c3aed" />
                              <text x={10} y={4} fontSize="12" fill="#374151">{t('feed.charts.legend.sgr')}</text>
                              <circle cx={64} cy={0} r={5} fill="#ea580c" />
                              <text x={74} y={4} fontSize="12" fill="#374151">{t('feed.charts.legend.fcr')}</text>
                            </g>

                            {/* Points */}
                            {points.map((p) => (
                              <g key={p.week}>
                                <circle cx={xFor(p.sfr)} cy={yLeftFor(p.sgr)} r={5} fill="#7c3aed">
                                  <title>{`${p.weekDisplay}\nSFR: ${p.sfr.toFixed(2)}%\nSGR: ${p.sgr.toFixed(2)}%/day`}</title>
                                </circle>
                                <circle cx={xFor(p.sfr)} cy={yRightFor(p.fcr)} r={5} fill="#ea580c">
                                  <title>{`${p.weekDisplay}\nSFR: ${p.sfr.toFixed(2)}%\nFCR: ${p.fcr.toFixed(fcrDecimals)}`}</title>
                                </circle>
                              </g>
                            ))}
                          </svg>
                        </div>
                      )
                    })()}
                  </div>
                </>
              )}
            </>
          )}
              </div>
            ) : null}

            {showSplitKpiCards && feedKPIs.length > 0 ? (
              <div style={{ gridColumn: '1 / -1' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(520px, 1fr))',
                    gap: '20px',
                    marginBottom: '30px'
                  }}
                >
                  {desktopBelowKpiCards.map(renderKpiCard)}
                </div>

                {renderLoadOlderWeeks()}
                {renderSummaryStats()}
              </div>
            ) : null}
          </div>
        </>
      )}

      {(!farmId || !productionUnitId || !isSmallScreen || activePanel === 'analyze') ? (
        <>

      {/* Feed Planning Information */}
      <div style={{ 
        marginTop: '30px', 
        padding: '25px', 
        backgroundColor: '#f8fafc', 
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ marginTop: 0, color: '#1e293b', marginBottom: '20px' }}>{t('feed.guide.title')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div>
            <h4 style={{ color: '#0284c7', marginBottom: '10px' }}>{t('feed.guide.sfr.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('feed.guide.label.definition')}</strong> {t('feed.guide.sfr.definition')}<br />
              <strong>{t('feed.guide.label.calculation')}</strong> {t('feed.guide.sfr.calculation')}<br />
              <strong>{t('feed.guide.label.temperatureDependency')}</strong> {t('feed.guide.sfr.temperatureDependency')}
            </p>
          </div>
          <div>
            <h4 style={{ color: '#ea580c', marginBottom: '10px' }}>{t('feed.guide.fcr.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('feed.guide.label.definition')}</strong> {t('feed.guide.fcr.definition')}<br />
              <strong>{t('feed.guide.label.calculation')}</strong> {t('feed.guide.fcr.calculation')}<br />
              <strong>{t('feed.guide.fcr.good')}</strong> {t('feed.guide.fcr.goodValue')}<br />
              <strong>{t('feed.guide.fcr.requires')}</strong> {t('feed.guide.fcr.requiresValue')}
            </p>
          </div>
          <div>
            <h4 style={{ color: '#16a34a', marginBottom: '10px' }}>{t('feed.guide.sgr.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('feed.guide.label.definition')}</strong> {t('feed.guide.sgr.definition')}<br />
              <strong>{t('feed.guide.label.calculation')}</strong> {t('feed.guide.sgr.calculation')}<br />
              <strong>{t('feed.guide.sgr.example')}</strong> {t('feed.guide.sgr.exampleValue')}<br />
              <strong>{t('feed.guide.sgr.classic')}</strong> {t('feed.guide.sgr.classicValue')}
            </p>
          </div>
          <div>
            <h4 style={{ color: '#059669', marginBottom: '10px' }}>{t('feed.guide.biomass.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('feed.guide.biomass.formula')}</strong> {t('feed.guide.biomass.formulaValue', { stockMetric: stockMetricLabel })}<br />
              <strong>{t('feed.guide.biomass.units')}</strong> {t('feed.guide.biomass.unitsValue')}<br />
              <strong>{t('feed.guide.biomass.purpose')}</strong> {t('feed.guide.biomass.purposeValue')}
            </p>
          </div>
          <div>
            <h4 style={{ color: '#7c3aed', marginBottom: '10px' }}>{t('feed.guide.quality.title')}</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              <strong>{t('feed.guide.quality.completeLabel')}</strong> {t('feed.guide.quality.completeValue')}<br />
              <strong>{t('feed.guide.quality.incompleteLabel')}</strong> {t('feed.guide.quality.incompleteValue')}<br />
              <strong>{t('feed.guide.quality.impactLabel')}</strong> {t('feed.guide.quality.impactValue')}
            </p>
          </div>
        </div>
        
        {/* Findings #143: the generic FCR interpretation was removed — FCR targets depend on species and stage. */}
      </div>
        </>
      ) : null}
      </div>
  </div>
  )
}
